import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import api, { portfolioLabAPI, portfolioRegistryAPI } from '../../../services/api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  LineController,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import StochasticChart from '../../../components/StochasticChart';
import BoxPlotChart from '../../../components/BoxPlotChart';
import ReturnsHistogramChart from '../../../components/ReturnsHistogramChart';
import { exportComparisonToExcel } from '../exportComparison';
import { exportPortfToExcel } from '../exportPortf';
import { useAuth } from '../../../contexts/AuthContext';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, LineController, PointElement, Title, Tooltip, Legend);

const COMPARISON_COLOURS = [
  '#072040', '#599BE3', '#f0a500', '#91CF9B', '#874564',
  '#39AEC5', '#305496', '#FFC000', '#AA8090', '#011E41',
];

// Single-metric vertical bar chart: one bar per compared portfolio, in the
// user-defined display order (same order as every other chart/table). Values are
// read via getValue (decimals) and shown as percentages. Used for Expected
// Return / Volatility / Max Drawdown / Return-to-Risk.
const METRIC_BAR_COLOUR = '#072040';

// Wrap a label onto multiple lines at word boundaries (Chart.js renders an array
// of strings as stacked tick lines), so long portfolio names stay readable.
const wrapLabel = (name, maxLen = 14, maxLines = 3) => {
  const words = String(name).split(/\s+/);
  const lines = [];
  let cur = '';
  for (const w of words) {
    if (!cur) cur = w;
    else if (`${cur} ${w}`.length <= maxLen) cur = `${cur} ${w}`;
    else { lines.push(cur); cur = w; }
  }
  if (cur) lines.push(cur);
  if (lines.length > maxLines) {
    const kept = lines.slice(0, maxLines);
    kept[maxLines - 1] = `${kept[maxLines - 1].slice(0, maxLen - 1)}…`;
    return kept;
  }
  return lines;
};

const MetricBarChart = ({ title, portfolios, getValue }) => {
  const rows = portfolios
    .map((p) => ({ name: p.name, value: getValue(p) }))
    .filter((r) => r.value != null && Number.isFinite(r.value))
    .map((r) => ({ name: r.name, value: r.value * 100 }));

  const fmt = (v) => (v == null ? '-' : `${Number(v).toFixed(1)}%`);

  return (
    <div className="chart-cell">
      <Bar
        data={{
          labels: rows.map((r) => wrapLabel(r.name)),
          datasets: [{
            label: title,
            data: rows.map((r) => +r.value.toFixed(2)),
            backgroundColor: METRIC_BAR_COLOUR,
          }],
        }}
        options={{
          responsive: true,
          maintainAspectRatio: true,
          aspectRatio: 1.4,
          plugins: {
            title: { display: true, text: title, color: '#072040', font: { size: 13, weight: 'bold' }, padding: { bottom: 4 } },
            legend: { display: false },
            tooltip: { callbacks: { label: (ctx) => `${rows[ctx.dataIndex]?.name}: ${fmt(ctx.parsed.y)}` } },
          },
          scales: {
            x: { grid: { display: false }, ticks: { font: { size: 11 }, maxRotation: 0, minRotation: 0, autoSkip: false } },
            y: { grid: { color: 'rgba(0,0,0,0.06)' }, ticks: { font: { size: 11 }, callback: (v) => fmt(v) } },
          },
        }}
      />
    </div>
  );
};

const ASSET_CLASS_COLORS = {
  'Australian Equities': '#011E41',
  'International Equities': '#5E819C',
  'Global Equities': '#5E819C',
  'Global Equities - H': '#5E819C',
  'Real Estate': '#A6C6C1',
  'Infrastructure': '#337B91',
  'Cash': '#EFE6D5',
  'Government Bonds': '#F3BD48',
  'Fixed Income - D': '#F3BD48',
  'Corporate Debt': '#AA8090',
  'Private Debt': '#874564',
  'Private Equity': '#6C757D',
  'Real Assets (Private Market)': '#127080',
  'Alternatives': '#9B59B6',
  'Emerging Markets': '#34495E',
};

const getAssetClassColor = (assetClass, index) => {
  if (ASSET_CLASS_COLORS[assetClass]) return ASSET_CLASS_COLORS[assetClass];
  const fallback = ['#072040', '#f0a500', '#599BE3', '#91CF9B', '#E74C3C', '#9B59B6', '#1ABC9C', '#E67E22', '#34495E', '#7F8C8D'];
  return fallback[index % fallback.length];
};

const getBetaColor = (value) => {
  if (value === null || value === undefined) return 'transparent';
  const capped = Math.min(Math.abs(value), 1.5);
  const intensity = capped / 1.5;
  if (value > 0) return `rgba(89, 155, 227, ${0.15 + intensity * 0.65})`;
  if (value < 0) return `rgba(231, 76, 60, ${0.15 + intensity * 0.65})`;
  return 'transparent';
};

const abbreviateFactorName = (name) => {
  if (name.startsWith('Style_')) return 'S:' + name.substring(6);
  if (name.startsWith('Tilt_')) return 'T:' + name.substring(5);
  return name;
};

// SAC client-friendly names come from the live taxonomy (sac_name column,
// admin-managed via Investment Config → Sub Asset Classes). Used as a runtime
// helper below — see the resolveSacName helper which falls back to the SAC
// code when no friendly name is set.

const PortfolioComparison = ({ savedPortfolios, onRefreshPortfolios }) => {
  const { user } = useAuth();
  const isDrummondUser = user?.is_drummond_user;
  const [registryData, setRegistryData] = useState(null);
  const [selectedCustomIds, setSelectedCustomIds] = useState([]);
  const [selectedModelCodes, setSelectedModelCodes] = useState([]);
  const [selectedSAACodes, setSelectedSAACodes] = useState([]);
  const [saaPortfolios, setSaaPortfolios] = useState([]);
  const [loadingSAA, setLoadingSAA] = useState(true);
  const [saaYears, setSaaYears] = useState([]);        // available SAA vintages (desc)
  const [activeSaaFY, setActiveSaaFY] = useState(null); // the live SAA year
  const [saaVintage, setSaaVintage] = useState(null);   // selected vintage (defaults to live)
  const [cmaScenarios, setCmaScenarios] = useState([]);          // CMA scenarios for analytics
  const [selectedScenarioId, setSelectedScenarioId] = useState(null); // null = default scenario
  const scenarioIdRef = useRef(null);                  // read inside fetchPortfolioAnalytics
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingModels, setLoadingModels] = useState(true);
  const [factorData, setFactorData] = useState(null);
  const [loadingFactors, setLoadingFactors] = useState(false);
  const [benchmarkKey, setBenchmarkKey] = useState(null);
  const [expandedSacs, setExpandedSacs] = useState(new Set());
  const [factorViewMode, setFactorViewMode] = useState('table'); // 'table' or 'chart' (Factor Exposures tab)
  const [analyticsViewMode, setAnalyticsViewMode] = useState('table'); // 'table' or 'chart' (Portfolio Analytics tab)
  const [riskViewMode, setRiskViewMode] = useState('table'); // 'table' or 'chart' (Risk Decomposition tab)
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [holdingsView, setHoldingsView] = useState('holdings'); // 'holdings', 'sac', 'assetClass'
  const [compareSection, setCompareSection] = useState('holdings'); // 'holdings' | 'factors' | 'analytics' | 'risk' | 'backtest'
  const [backtestData, setBacktestData] = useState(null);
  const [loadingBacktest, setLoadingBacktest] = useState(false);
  const [backtestError, setBacktestError] = useState(null);
  const [overlayPortfolioCodes, setOverlayPortfolioCodes] = useState([]);
  const [overlaySeries, setOverlaySeries] = useState({}); // keyed by portfolio_code
  const [loadingOverlay, setLoadingOverlay] = useState(false);

  useEffect(() => {
    loadRegistry();
    loadSAAPortfolios();
    // CMA scenarios for the analytics dropdown. Non-admin users have no access to
    // this endpoint — silently ignore so the dropdown just doesn't appear.
    api.get('/api/investment-config/cma/scenarios')
      .then((res) => setCmaScenarios(res.data.scenarios || []))
      .catch(() => {});
  }, []);

  // Keep the ref in step so fetchPortfolioAnalytics can read the current scenario
  // without changing its identity (which would re-trigger factor/backtest fetches).
  useEffect(() => { scenarioIdRef.current = selectedScenarioId; }, [selectedScenarioId]);

  const loadRegistry = async () => {
    setLoadingModels(true);
    try {
      const data = await portfolioRegistryAPI.getRegistry('portfolio_lab');
      setRegistryData(data);
    } catch (err) {
      console.error('Failed to load portfolio registry:', err);
    } finally {
      setLoadingModels(false);
    }
  };

  // Flatten registry suites into a list of model portfolios
  const modelPortfolios = useMemo(() => {
    if (!registryData?.suites) return [];
    return registryData.suites.flatMap(suite =>
      suite.portfolios.map(p => ({
        portfolio_code: p.portfolio_code,
        display_name: p.display_name || p.portfolio_code,
        suite_name: suite.label,
      }))
    );
  }, [registryData]);

  const loadSAAPortfolios = async () => {
    setLoadingSAA(true);
    try {
      const data = await portfolioLabAPI.getSAAPortfolios();
      setSaaPortfolios(data.portfolios || []); // [{code, display_name, suite}]
      try {
        const v = await portfolioLabAPI.getSAAVintages();
        const fys = v.financial_years || [];
        setSaaYears(fys);
        setActiveSaaFY(v.active_financial_year ?? null);
        // Default the vintage to the live year (fallback to the newest available).
        setSaaVintage(v.active_financial_year ?? fys[0] ?? null);
      } catch (vErr) {
        console.error('Failed to load SAA vintages:', vErr);
      }
    } catch (err) {
      console.error('Failed to load SAA portfolios:', err);
    } finally {
      setLoadingSAA(false);
    }
  };

  // Build a SAC code → friendly label map from the registry's asset_classes
  // payload (each entry is {code, label} where label = sac_name fallback to
  // the SAC code). Used by chart labels and SAC block headers.
  const sacDisplayNames = useMemo(() => {
    const map = {};
    for (const entry of (registryData?.asset_classes || [])) {
      if (entry && entry.code) map[entry.code] = entry.label || entry.code;
    }
    return map;
  }, [registryData]);

  // Group SAA portfolios by suite for display
  const saaSuites = useMemo(() => {
    const groups = {};
    saaPortfolios.forEach(p => {
      const suite = p.suite || 'Other';
      if (!groups[suite]) groups[suite] = [];
      groups[suite].push(p);
    });
    return Object.entries(groups).sort(([a], [b]) => {
      if (a === 'Strategic Series') return -1;
      if (b === 'Strategic Series') return 1;
      return a.localeCompare(b);
    });
  }, [saaPortfolios]);

  const toggleSAA = (code) => {
    setSelectedSAACodes(prev =>
      prev.includes(code) ? prev.filter(x => x !== code) : [...prev, code]
    );
  };

  const toggleCustom = (id) => {
    setSelectedCustomIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleModel = (code) => {
    setSelectedModelCodes(prev =>
      prev.includes(code) ? prev.filter(x => x !== code) : [...prev, code]
    );
  };

  const handleCompare = async () => {
    if (totalSelected < 1) return;
    setLoading(true);
    setComparisonData(null);

    try {
      // Fetch custom portfolio holdings
      const customPortfolios = await Promise.all(
        selectedCustomIds.map(id => portfolioLabAPI.getPortfolio(id))
      );

      // Fetch model portfolio holdings via Shadow_Data endpoint
      let rawHoldings = [];
      if (selectedModelCodes.length > 0) {
        const holdingsRes = await api.get(`/api/portfolio/portfolios/holdings?portfolio_codes=${selectedModelCodes.join(',')}`);
        rawHoldings = holdingsRes.data.holdings || [];
      }

      // Build per-portfolio holdings from raw data
      const perPortfolio = {};
      selectedModelCodes.forEach(code => { perPortfolio[code] = []; });
      rawHoldings.forEach(h => {
        Object.entries(h.weights || {}).forEach(([code, weight]) => {
          if (perPortfolio[code]) {
            perPortfolio[code].push({
              fund_code: h.tr_code || '',
              fund_name: h.asset_name,
              weight,
              asset_class: h.asset_class_group,
            });
          }
        });
      });

      // Fetch SAA holdings
      let saaData = {};
      if (selectedSAACodes.length > 0) {
        const saaRes = await portfolioLabAPI.getSAAHoldings(selectedSAACodes, saaVintage);
        saaData = saaRes.portfolios || {};
      }

      // Collect all unique TR codes across all portfolios for asset class lookup
      const allTrCodes = new Set();
      customPortfolios.forEach(p => (p.holdings || []).forEach(h => { if (h.fund_code) allTrCodes.add(h.fund_code); }));
      rawHoldings.forEach(h => { if (h.tr_code) allTrCodes.add(h.tr_code); });
      Object.values(saaData).forEach(p => (p.holdings || []).forEach(h => { if (h.fund_code) allTrCodes.add(h.fund_code); }));

      // Look up asset classes from AP List
      let assetClassLookup = {};
      if (allTrCodes.size > 0) {
        const lookupRes = await portfolioLabAPI.assetClassLookup([...allTrCodes]);
        assetClassLookup = lookupRes.lookup || {};
      }

      setComparisonData({
        custom: customPortfolios,
        model: selectedModelCodes.map(code => ({
          code,
          name: modelPortfolios.find(p => p.portfolio_code === code)?.display_name || code,
          holdings: perPortfolio[code] || [],
        })),
        saa: selectedSAACodes.map(code => {
          const saaInfo = saaPortfolios.find(p => p.code === code);
          const baseName = saaInfo?.display_name || code;
          return {
            code,
            name: `SAA ${baseName}`,
            holdings: saaData[code]?.holdings || [],
            sac_weights: saaData[code]?.sac_weights || null,
            currency_exposure: saaData[code]?.currency_exposure,
          };
        }),
        assetClassLookup,
      });
    } catch (err) {
      console.error('Failed to compare portfolios:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch factor exposures when comparisonData is ready
  const fetchFactorExposures = useCallback(async (data) => {
    if (!data) return;
    setLoadingFactors(true);
    setFactorData(null);
    setBenchmarkKey(null);
    setExpandedSacs(new Set());

    try {
      const portfoliosPayload = [
        ...data.custom.map(p => ({
          key: `custom-${p.id}`,
          name: p.name,
          holdings: (p.holdings || []).map(h => ({
            fund_code: h.fund_code,
            weight: h.weight,
          })),
        })),
        ...data.model.map(p => ({
          key: `model-${p.code}`,
          name: p.name,
          holdings: (p.holdings || []).map(h => ({
            fund_code: h.fund_code,
            weight: h.weight,
          })),
        })),
        ...(data.saa || []).map(p => ({
          key: `saa-${p.code}`,
          name: p.name,
          holdings: (p.holdings || []).map(h => ({
            fund_code: h.fund_code,
            weight: h.weight,
          })),
        })),
      ];

      if (portfoliosPayload.length > 0) {
        const result = await portfolioLabAPI.getFactorExposures(portfoliosPayload);
        setFactorData(result);
      }
    } catch (err) {
      console.error('Failed to fetch factor exposures:', err);
    } finally {
      setLoadingFactors(false);
    }
  }, []);

  // Fetch portfolio analytics (blender calculations) when comparisonData is ready
  const fetchPortfolioAnalytics = useCallback(async (data) => {
    if (!data) return;
    setLoadingAnalytics(true);
    setAnalyticsData(null);

    try {
      const portfoliosPayload = [
        ...data.custom.map(p => ({
          key: `custom-${p.id}`,
          name: p.name,
          holdings: (p.holdings || []).map(h => ({
            fund_code: h.fund_code,
            weight: h.weight,
          })),
          // Return objective travels with the saved portfolio so the CMA
          // process can compute objective rate, excess and P(meet objective).
          objective_type: p.objective_type ?? undefined,
          return_objective: p.return_objective ?? undefined,
          time_horizon: p.time_horizon ?? undefined,
        })),
        ...data.model.map(p => ({
          key: `model-${p.code}`,
          name: p.name,
          holdings: (p.holdings || []).map(h => ({
            fund_code: h.fund_code,
            weight: h.weight,
          })),
          // Resolve the objective from the registry (model code = portfolio_code).
          registry_code: p.code,
        })),
        ...(data.saa || []).map(p => ({
          key: `saa-${p.code}`,
          name: p.name,
          holdings: (p.holdings || []).map(h => ({
            fund_code: h.fund_code,
            weight: h.weight,
          })),
          // Resolve the objective from the registry (SAA code = blender_code).
          registry_code: p.code,
          // Authoritative SAC weights from portfolio_sac_weights. The analytics
          // endpoint uses these directly so SACs without a fund_tr_code
          // mapping (private-market SACs) aren't dropped via the fund→SAC path.
          sac_weights: p.sac_weights || undefined,
          currency_exposure: p.currency_exposure ?? undefined,
        })),
      ];

      if (portfoliosPayload.length > 0) {
        const result = await portfolioLabAPI.getPortfolioAnalytics(
          portfoliosPayload, null, scenarioIdRef.current);
        setAnalyticsData(result);
      }
    } catch (err) {
      console.error('Failed to fetch portfolio analytics:', err);
    } finally {
      setLoadingAnalytics(false);
    }
  }, []);

  // Backtest each portfolio over the global narrowest date intersection
  const fetchBacktest = useCallback(async (data) => {
    if (!data) return;
    setLoadingBacktest(true);
    setBacktestData(null);
    setBacktestError(null);
    setOverlayPortfolioCodes([]);
    setOverlaySeries({});

    try {
      const portfoliosPayload = [
        ...data.custom.map(p => ({
          key: `custom-${p.id}`,
          name: p.name,
          holdings: (p.holdings || []).map(h => ({
            fund_code: h.fund_code,
            weight: h.weight,
          })),
        })),
        ...data.model.map(p => ({
          key: `model-${p.code}`,
          name: p.name,
          holdings: (p.holdings || []).map(h => ({
            fund_code: h.fund_code,
            weight: h.weight,
          })),
        })),
        ...(data.saa || []).map(p => ({
          key: `saa-${p.code}`,
          name: p.name,
          holdings: (p.holdings || []).map(h => ({
            fund_code: h.fund_code,
            weight: h.weight,
          })),
        })),
      ];

      if (portfoliosPayload.length > 0) {
        const result = await portfolioLabAPI.backtest(portfoliosPayload);
        setBacktestData(result);
      }
    } catch (err) {
      console.error('Failed to backtest portfolios:', err);
      setBacktestError(err.response?.data?.detail || err.message);
    } finally {
      setLoadingBacktest(false);
    }
  }, []);

  useEffect(() => {
    if (comparisonData) {
      fetchFactorExposures(comparisonData);
      fetchPortfolioAnalytics(comparisonData);
      fetchBacktest(comparisonData);
    }
  }, [comparisonData, fetchFactorExposures, fetchPortfolioAnalytics, fetchBacktest]);

  // Re-run only the (CMA-dependent) analytics when the scenario changes — factor
  // exposures and backtest don't use CMAs, so leave them untouched.
  useEffect(() => {
    if (comparisonData) fetchPortfolioAnalytics(comparisonData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedScenarioId]);

  // Fetch actual model-portfolio performance for any newly-selected overlays
  useEffect(() => {
    if (!backtestData?.common_start_date) return;
    const missing = overlayPortfolioCodes.filter(c => !overlaySeries[c]);
    if (missing.length === 0) return;

    let cancelled = false;
    setLoadingOverlay(true);
    portfolioLabAPI.getActualPerformance(missing, backtestData.common_start_date)
      .then(d => {
        if (cancelled) return;
        setOverlaySeries(prev => ({
          ...prev,
          ...Object.fromEntries((d.series || []).map(s => [s.portfolio_code, s])),
        }));
      })
      .catch(err => {
        console.error('Failed to load actual performance:', err);
      })
      .finally(() => {
        if (!cancelled) setLoadingOverlay(false);
      });

    return () => { cancelled = true; };
  }, [overlayPortfolioCodes, backtestData?.common_start_date, overlaySeries]);

  const toggleSacExpanded = (sac) => {
    setExpandedSacs(prev => {
      const next = new Set(prev);
      if (next.has(sac)) next.delete(sac);
      else next.add(sac);
      return next;
    });
  };

  // User-defined display order of the compared portfolios (array of keys). Drives
  // the order of every chart and table column. Resets to the natural order
  // (custom → model → SAA) whenever a new comparison is run.
  const [portfolioOrder, setPortfolioOrder] = useState([]);

  const naturalPortfolioKeys = useMemo(() => {
    if (!comparisonData) return [];
    return [
      ...comparisonData.custom.map(p => `custom-${p.id}`),
      ...comparisonData.model.map(p => `model-${p.code}`),
      ...(comparisonData.saa || []).map(p => `saa-${p.code}`),
    ];
  }, [comparisonData]);

  useEffect(() => {
    setPortfolioOrder(naturalPortfolioKeys);
  }, [naturalPortfolioKeys]);

  const movePortfolio = (key, dir) => {
    setPortfolioOrder(prev => {
      const arr = prev.length ? [...prev] : [...naturalPortfolioKeys];
      const i = arr.indexOf(key);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= arr.length) return arr;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return arr;
    });
  };

  // Build a unified list of all funds across all selected portfolios, grouped by asset class
  const comparisonTable = useMemo(() => {
    if (!comparisonData) return null;

    const assetClassLookup = comparisonData.assetClassLookup || {};

    const saaList = comparisonData.saa || [];
    const allPortfolios = [
      ...comparisonData.custom.map((p, i) => ({
        key: `custom-${p.id}`,
        name: p.name,
        type: 'custom',
        holdings: p.holdings,
        colour: COMPARISON_COLOURS[i % COMPARISON_COLOURS.length],
      })),
      ...comparisonData.model.map((p, i) => ({
        key: `model-${p.code}`,
        name: p.name,
        type: 'model',
        holdings: p.holdings,
        colour: COMPARISON_COLOURS[(comparisonData.custom.length + i) % COMPARISON_COLOURS.length],
      })),
      ...saaList.map((p, i) => ({
        key: `saa-${p.code}`,
        name: p.name,
        type: 'saa',
        holdings: p.holdings,
        currency_exposure: p.currency_exposure,
        colour: COMPARISON_COLOURS[(comparisonData.custom.length + comparisonData.model.length + i) % COMPARISON_COLOURS.length],
      })),
    ];

    // Apply the user-defined display order (each portfolio keeps its own colour;
    // only the sequence changes). Unknown keys fall to the end.
    if (portfolioOrder && portfolioOrder.length) {
      const rank = (k) => {
        const idx = portfolioOrder.indexOf(k);
        return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
      };
      allPortfolios.sort((a, b) => rank(a.key) - rank(b.key));
    }

    // Collect all unique funds with asset class
    // Use TR code as key, falling back to fund name for holdings without a code (e.g. cash)
    const fundMap = new Map();
    allPortfolios.forEach(portfolio => {
      (portfolio.holdings || []).forEach(h => {
        const code = h.fund_code || h.tr_code || h.code;
        const name = h.fund_name || h.name || code;
        const key = code || name;
        const ac = h.asset_class || assetClassLookup[code] || 'Other';
        if (key && !fundMap.has(key)) {
          fundMap.set(key, { name, code: code || '', assetClass: ac });
        }
      });
    });

    // Build rows with weights per portfolio (sum duplicates, e.g. same fund mapped from multiple SACs)
    const rows = Array.from(fundMap.entries()).map(([key, { name, code, assetClass }]) => {
      const weights = {};
      allPortfolios.forEach(portfolio => {
        const matches = (portfolio.holdings || []).filter(h => {
          const hCode = h.fund_code || h.tr_code || h.code;
          const hKey = hCode || h.fund_name || h.name;
          return hKey === key;
        });
        if (matches.length > 0) {
          weights[portfolio.key] = matches.reduce((sum, h) => sum + (h.weight != null ? h.weight : h.percentage || 0), 0);
        } else {
          weights[portfolio.key] = null;
        }
      });
      return { code: code || key, name, assetClass, weights };
    });

    // Group rows by asset class
    const groups = {};
    rows.forEach(row => {
      if (!groups[row.assetClass]) groups[row.assetClass] = [];
      groups[row.assetClass].push(row);
    });

    // Sort each group by weight in first portfolio (descending)
    const firstKey = allPortfolios[0]?.key;
    if (firstKey) {
      Object.values(groups).forEach(groupRows => {
        groupRows.sort((a, b) => (b.weights[firstKey] || 0) - (a.weights[firstKey] || 0));
      });
    }

    // Calculate asset class totals per portfolio
    const assetClassTotals = {};
    Object.entries(groups).forEach(([ac, groupRows]) => {
      assetClassTotals[ac] = {};
      allPortfolios.forEach(p => {
        assetClassTotals[ac][p.key] = groupRows.reduce((sum, row) => sum + (row.weights[p.key] || 0), 0);
      });
    });

    return { portfolios: allPortfolios, groups, rows, assetClassTotals };
  }, [comparisonData, portfolioOrder]);

  // Actual model portfolios from the registry that can be overlaid on the
  // backtest chart via factsheet performance. The dropdown lists every model
  // portfolio regardless of whether it's in the current comparison.
  const eligibleOverlayPortfolios = useMemo(() => {
    const comparedByCode = new Map();
    (comparisonTable?.portfolios || [])
      .filter(p => p.type === 'model' && p.key.startsWith('model-'))
      .forEach(p => {
        comparedByCode.set(p.key.slice('model-'.length), p);
      });

    return modelPortfolios.map((p, i) => {
      const compared = comparedByCode.get(p.portfolio_code);
      // Compared portfolios reuse their comparison colour; otherwise pick from
      // the back of the palette to minimise clashes with comparison series.
      const colour = compared
        ? compared.colour
        : COMPARISON_COLOURS[(COMPARISON_COLOURS.length - 1 - i + COMPARISON_COLOURS.length) % COMPARISON_COLOURS.length];
      return {
        portfolio_code: p.portfolio_code,
        name: compared?.name || p.display_name || p.portfolio_code,
        colour,
        inComparison: !!compared,
        suite: p.suite_name,
      };
    });
  }, [comparisonTable, modelPortfolios]);

  const totalSelected = selectedCustomIds.length + selectedModelCodes.length + selectedSAACodes.length;

  // Export every Compare tab (including chart-only series) to a multi-sheet xlsx.
  const handleExportExcel = () => {
    const overlays = Object.entries(overlaySeries || {})
      .filter(([, s]) => s && (s.cumulative_series?.length || s.metrics))
      .map(([code, s]) => ({
        code,
        name: modelPortfolios.find(m => m.portfolio_code === code)?.display_name || code,
        cumulative_series: s.cumulative_series,
        metrics: s.metrics,
      }));
    exportComparisonToExcel({
      comparisonTable,
      analyticsData,
      factorData,
      backtestData,
      overlays,
      benchmarkKey,
      benchmarkName: benchmarkKey
        ? comparisonTable?.portfolios?.find(p => p.key === benchmarkKey)?.name
        : null,
      saaVintage,
      scenarioName: selectedScenarioId
        ? (cmaScenarios.find(s => s.id === selectedScenarioId)?.name || `Scenario ${selectedScenarioId}`)
        : 'Default',
    });
  };

  // Drummond-only: export current holdings replicated across quarter-ends as a
  // Refinitiv PORT upload datafile (Portfolio_Name, Portfolio_Code, Fund_Code,
  // Weight, Date). Cash is remapped to .ONEAUD; fund codes are the Shadow
  // TR_Codes (Lipper / RIC) already carried on each holding.
  const handleExportPortf = () => {
    exportPortfToExcel({ comparisonTable });
  };

  return (
    <div className="portfolio-comparison">
      {/* Global settings — apply across all Compare tabs (analytics + risk use the
          CMA scenario; SAA holdings use the vintage). */}
      {(saaYears.length > 0 || cmaScenarios.length > 0) && (
        <div className="d-flex align-items-center flex-wrap gap-4 mb-3 p-2"
          style={{ background: 'var(--light-color)', borderRadius: 6 }}>
          {saaYears.length > 0 && (
            <div className="d-flex align-items-center gap-2">
              <label className="mb-0" style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--primary-color)' }}>
                SAA vintage
              </label>
              <select className="form-select form-select-sm" style={{ width: 150, fontSize: '0.85rem' }}
                value={saaVintage ?? ''}
                onChange={(e) => setSaaVintage(parseInt(e.target.value, 10))}
                title="Which SAA financial year to compare against (used when fetching SAA portfolios)">
                {saaYears.map((fy) => (
                  <option key={fy} value={fy}>FY{fy}{fy === activeSaaFY ? ' (live)' : ''}</option>
                ))}
              </select>
            </div>
          )}
          {cmaScenarios.length > 0 && (
            <div className="d-flex align-items-center gap-2">
              <label className="mb-0" style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--primary-color)' }}>
                CMA scenario
              </label>
              <select className="form-select form-select-sm" style={{ maxWidth: 260, fontSize: '0.85rem' }}
                value={selectedScenarioId || ''}
                onChange={(e) => setSelectedScenarioId(parseInt(e.target.value, 10) || null)}
                title="Capital market assumptions used for Portfolio Analytics and Risk Decomposition">
                <option value="">Default</option>
                {cmaScenarios.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}{s.is_default ? ' (Default)' : ''}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}
      <div className="row">
        {/* Custom portfolios selector */}
        <div className="col-md-4 mb-3">
          <h6 style={{ color: 'var(--primary-color)', fontWeight: 700, fontSize: '0.9rem' }}>
            Saved Portfolios
          </h6>
          {savedPortfolios.length === 0 ? (
            <p className="text-muted" style={{ fontSize: '0.82rem' }}>
              No saved portfolios. Build and save one in the Build tab first.
            </p>
          ) : (
            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
              {(() => {
                const groups = {};
                savedPortfolios.forEach(p => {
                  const client = p.client || 'Unassigned';
                  if (!groups[client]) groups[client] = [];
                  groups[client].push(p);
                });
                const sortedKeys = Object.keys(groups).sort((a, b) => {
                  if (a === 'Unassigned') return 1;
                  if (b === 'Unassigned') return -1;
                  return a.localeCompare(b);
                });
                return sortedKeys.map(client => (
                  <div key={client} className="mb-2">
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--gray-color)', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
                      {client}
                    </div>
                    {groups[client].map(p => (
                      <label key={p.id} className="d-flex align-items-center gap-2 mb-1" style={{ cursor: 'pointer', fontSize: '0.85rem', paddingLeft: '0.5rem' }}>
                        <input
                          type="checkbox"
                          checked={selectedCustomIds.includes(p.id)}
                          onChange={() => toggleCustom(p.id)}
                        />
                        <span>{p.name}</span>
                        <span className="text-muted" style={{ fontSize: '0.75rem' }}>({p.fund_count} funds)</span>
                      </label>
                    ))}
                  </div>
                ));
              })()}
            </div>
          )}
        </div>

        {/* Model portfolios selector */}
        <div className="col-md-4 mb-3">
          <h6 style={{ color: 'var(--primary-color)', fontWeight: 700, fontSize: '0.9rem' }}>
            Model Portfolios
          </h6>
          {loadingModels ? (
            <div className="d-flex align-items-center gap-2" style={{ fontSize: '0.82rem' }}>
              <div className="spinner-border spinner-border-sm text-primary" role="status" />
              <span className="text-muted">Loading model portfolios...</span>
            </div>
          ) : modelPortfolios.length === 0 ? (
            <p className="text-muted" style={{ fontSize: '0.82rem' }}>No model portfolios available.</p>
          ) : (
            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
              {registryData.suites.map(suite => (
                <div key={suite.label} className="mb-2">
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--gray-color)', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
                    {suite.label}
                  </div>
                  {suite.portfolios.map(p => (
                    <label key={p.portfolio_code} className="d-flex align-items-center gap-2 mb-1" style={{ cursor: 'pointer', fontSize: '0.85rem', paddingLeft: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={selectedModelCodes.includes(p.portfolio_code)}
                        onChange={() => toggleModel(p.portfolio_code)}
                      />
                      <span>{p.display_name || p.portfolio_code}</span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* SAA portfolios selector */}
        <div className="col-md-4 mb-3">
          <h6 style={{ color: 'var(--primary-color)', fontWeight: 700, fontSize: '0.9rem' }}>
            SAA Portfolios
          </h6>
          {loadingSAA ? (
            <div className="d-flex align-items-center gap-2" style={{ fontSize: '0.82rem' }}>
              <div className="spinner-border spinner-border-sm text-primary" role="status" />
              <span className="text-muted">Loading SAA portfolios...</span>
            </div>
          ) : saaPortfolios.length === 0 ? (
            <p className="text-muted" style={{ fontSize: '0.82rem' }}>No SAA portfolios available.</p>
          ) : (
            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
              {saaSuites.map(([suite, portfolios]) => (
                <div key={suite} className="mb-2">
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--gray-color)', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
                    {suite}
                  </div>
                  {portfolios.map(p => (
                    <label key={p.code} className="d-flex align-items-center gap-2 mb-1" style={{ cursor: 'pointer', fontSize: '0.85rem', paddingLeft: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={selectedSAACodes.includes(p.code)}
                        onChange={() => toggleSAA(p.code)}
                      />
                      <span>{p.display_name}</span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="d-flex align-items-center gap-3 mb-4">
        <button
          className="btn"
          style={{ background: 'var(--primary-color)', color: 'var(--white-color)' }}
          onClick={handleCompare}
          disabled={totalSelected < 1 || loading}
        >
          {loading ? 'Comparing...' : `Compare ${totalSelected} ${totalSelected === 1 ? 'Portfolio' : 'Portfolios'}`}
        </button>
        {totalSelected < 1 && (
          <span className="text-muted" style={{ fontSize: '0.82rem' }}>
            Select at least 1 portfolio to compare
          </span>
        )}
      </div>

      {/* Benchmark selector — shared across all Compare tabs */}
      {comparisonTable && (
        <div className="d-flex align-items-center flex-wrap gap-3 mb-2">
          <div className="benchmark-selector" style={{ marginBottom: 0 }}>
            <label style={{ fontWeight: 600, color: 'var(--primary-color)' }}>Benchmark:</label>
            <select
              value={benchmarkKey || ''}
              onChange={(e) => setBenchmarkKey(e.target.value || null)}
            >
              <option value="">None</option>
              {comparisonTable.portfolios.map(p => (
                <option key={p.key} value={p.key}>{p.name}</option>
              ))}
            </select>
          </div>
          <button
            className="btn btn-sm ms-auto"
            style={{ background: 'var(--secondary-color)', color: 'var(--primary-color)', fontWeight: 600 }}
            onClick={handleExportExcel}
            title="Export every Compare tab — including chart-only data (stochastic paths, distribution, backtest series) — to a multi-sheet Excel file"
          >
            <i className="bi bi-file-earmark-excel me-1" />Export to Excel
          </button>
          {isDrummondUser && (
            <button
              className="btn btn-sm"
              style={{ background: 'var(--primary-color)', color: 'var(--white-color)', fontWeight: 600 }}
              onClick={handleExportPortf}
              title="Drummond only: export current holdings replicated across quarter-ends (30 Sep 2019 to the last completed quarter) as a Refinitiv PORT upload datafile. Cash is remapped to .ONEAUD."
            >
              <i className="bi bi-file-earmark-spreadsheet me-1" />PORTF Excel
            </button>
          )}

          {compareSection === 'backtest' && eligibleOverlayPortfolios.length > 0 && (
            <div className="overlay-actual-selector">
              <label style={{ fontWeight: 600, color: 'var(--primary-color)' }}>
                Overlay actual performance:
              </label>
              <details className="overlay-actual-dropdown">
                <summary>
                  {overlayPortfolioCodes.length === 0
                    ? 'None selected'
                    : `${overlayPortfolioCodes.length} selected`}
                </summary>
                <div className="overlay-actual-menu">
                  {eligibleOverlayPortfolios.map(p => {
                    const checked = overlayPortfolioCodes.includes(p.portfolio_code);
                    return (
                      <label key={p.portfolio_code} className="overlay-actual-option">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            setOverlayPortfolioCodes(prev =>
                              checked
                                ? prev.filter(c => c !== p.portfolio_code)
                                : [...prev, p.portfolio_code]
                            );
                          }}
                        />
                        <span>{p.name}</span>
                      </label>
                    );
                  })}
                </div>
              </details>
              {loadingOverlay && (
                <span className="text-muted ms-2" style={{ fontSize: '0.78rem' }}>
                  Loading…
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Display-order reorder bar — drives the order of every chart and table */}
      {comparisonTable && comparisonTable.portfolios.length > 1 && (
        <div
          className="pl-order-bar"
          style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, margin: '4px 0 10px' }}
        >
          <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--primary-color)' }}>
            Display order:
          </span>
          {comparisonTable.portfolios.map((p, i) => (
            <span
              key={p.key}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4, background: '#fff',
                border: '1px solid #dee2e6', borderLeft: `4px solid ${p.colour}`,
                borderRadius: 4, padding: '2px 4px 2px 8px', fontSize: '0.78rem',
              }}
            >
              <span
                style={{ maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                title={p.name}
              >
                {p.name}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-light"
                style={{ padding: '0 6px', lineHeight: 1.3, borderColor: '#dee2e6' }}
                disabled={i === 0}
                onClick={() => movePortfolio(p.key, -1)}
                title="Move earlier"
              >
                ‹
              </button>
              <button
                type="button"
                className="btn btn-sm btn-light"
                style={{ padding: '0 6px', lineHeight: 1.3, borderColor: '#dee2e6' }}
                disabled={i === comparisonTable.portfolios.length - 1}
                onClick={() => movePortfolio(p.key, 1)}
                title="Move later"
              >
                ›
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Comparison sub-tabs */}
      {comparisonTable && (
        <ul className="nav nav-tabs compare-subtabs">
          {[
            { key: 'holdings', label: 'Holdings' },
            { key: 'factors', label: 'Factor Exposures' },
            { key: 'analytics', label: 'Portfolio Analytics' },
            { key: 'risk', label: 'Risk Decomposition' },
            { key: 'backtest', label: 'Backtesting' },
          ].map(t => (
            <li key={t.key} className="nav-item">
              <button
                className={`nav-link ${compareSection === t.key ? 'active' : ''}`}
                onClick={() => setCompareSection(t.key)}
              >
                {t.label}
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Holdings Comparison */}
      {comparisonTable && compareSection === 'holdings' && (
        <div>
          <div className="d-flex align-items-center justify-content-end mb-2">
            <div className="btn-group btn-group-sm">
              {[
                { key: 'holdings', label: 'Holdings' },
                { key: 'sac', label: 'Sub Asset Class' },
                { key: 'assetClass', label: 'Asset Class Group' },
              ].map(v => (
                <button
                  key={v.key}
                  className={`btn ${holdingsView === v.key ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setHoldingsView(v.key)}
                  style={{
                    fontSize: '0.75rem',
                    padding: '0.25rem 0.6rem',
                    ...(holdingsView === v.key
                      ? { backgroundColor: 'var(--primary-color)', borderColor: 'var(--primary-color)' }
                      : { color: 'var(--primary-color)', borderColor: 'var(--primary-color)' }),
                  }}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>

          {/* Holdings View */}
          {holdingsView === 'holdings' && (
            <div style={{ overflowX: 'auto' }}>
              <table className="holdings-table">
                <thead>
                  <tr>
                    <th className="name-col">Fund Name</th>
                    <th className="code-col">Code</th>
                    {comparisonTable.portfolios.map(p => {
                      const geared = comparisonTable.rows.some(row => (row.weights[p.key] || 0) < 0);
                      return (
                        <th key={p.key} className="weight-col" style={{ borderBottom: `3px solid ${p.colour}` }}>
                          <div>{p.name}</div>
                          <div style={{ fontSize: '0.65rem', fontWeight: 400, opacity: 0.8 }}>
                            {p.type === 'custom' ? 'Custom' : p.type === 'saa' ? 'SAA' : 'Model'}
                            {geared && (
                              <span
                                className="negative-chip"
                                title="Geared portfolio — holds negative (borrowed/short) weights, nets to 100%"
                              >GEARED</span>
                            )}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(comparisonTable.groups).map(([assetClass, groupRows], groupIndex) => (
                    <React.Fragment key={assetClass}>
                      <tr className="asset-class-header">
                        <td colSpan={2}>
                          <span
                            className="asset-class-indicator"
                            style={{ backgroundColor: getAssetClassColor(assetClass, groupIndex) }}
                          />
                          {assetClass}
                        </td>
                        {comparisonTable.portfolios.map(p => (
                          <td key={p.key} className="weight-col asset-class-total">
                            {comparisonTable.assetClassTotals[assetClass]?.[p.key]?.toFixed(1) || '0.0'}%
                          </td>
                        ))}
                      </tr>
                      {groupRows.map(row => (
                        <tr key={row.code} className="holding-row">
                          <td className="name-col">{row.name}</td>
                          <td className="code-col">{row.code}</td>
                          {comparisonTable.portfolios.map(p => (
                            <td
                              key={p.key}
                              className="weight-col"
                              style={row.weights[p.key] < 0
                                ? { color: '#dc3545', fontWeight: 700 } : undefined}
                              title={row.weights[p.key] < 0
                                ? 'Negative weight — geared (borrowed/short) position' : undefined}
                            >
                              {row.weights[p.key] != null
                                ? `${row.weights[p.key].toFixed(1)}%`
                                : <span style={{ color: 'var(--gray-color)' }}>-</span>
                              }
                            </td>
                          ))}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  <tr className="total-row">
                    <td colSpan={2}>Total (net)</td>
                    {comparisonTable.portfolios.map(p => {
                      const total = comparisonTable.rows.reduce((sum, row) => sum + (row.weights[p.key] || 0), 0);
                      return <td key={p.key} className="weight-col">{total.toFixed(1)}%</td>;
                    })}
                  </tr>
                  {/* Gross exposure — only shown when a geared portfolio is in the comparison */}
                  {comparisonTable.portfolios.some(p =>
                    comparisonTable.rows.some(row => (row.weights[p.key] || 0) < 0)) && (
                    <tr className="total-row">
                      <td colSpan={2}>Gross Exposure <span className="negative-chip">GEARED</span></td>
                      {comparisonTable.portfolios.map(p => {
                        const gross = comparisonTable.rows.reduce((sum, row) => sum + Math.abs(row.weights[p.key] || 0), 0);
                        const net = comparisonTable.rows.reduce((sum, row) => sum + (row.weights[p.key] || 0), 0);
                        const geared = gross - net > 0.05;
                        return (
                          <td key={p.key} className="weight-col"
                              style={geared ? { color: '#dc3545', fontWeight: 700 } : undefined}>
                            {gross.toFixed(1)}%
                          </td>
                        );
                      })}
                    </tr>
                  )}
                  <tr className="total-row" style={{ borderTop: '2px solid var(--border-color)' }}>
                    <td colSpan={2}>Growth Exposure</td>
                    {comparisonTable.portfolios.map(p => {
                      // Use the backend-computed value (taxonomy-aware via
                      // SubAssetClassConfig.growth_exposure); the previous
                      // hardcoded SAC list didn't know about the private-market
                      // SACs (GL_PE, GL_PRIVATE_*, AU_PRIVATE_*).
                      const growthVal = analyticsData?.portfolios?.[p.key]?.growth_exposure ?? null;
                      return (
                        <td key={p.key} className="weight-col">
                          {growthVal != null ? `${(growthVal * 100).toFixed(1)}%` : <span style={{ color: 'var(--gray-color)' }}>-</span>}
                        </td>
                      );
                    })}
                  </tr>
                  <tr className="total-row">
                    <td colSpan={2}>Currency Exposure</td>
                    {comparisonTable.portfolios.map(p => {
                      const saaCurrency = p.currency_exposure;
                      const analyticsCurrency = analyticsData?.portfolios?.[p.key]?.sac_weights?.CURRENCY;
                      const currencyVal = saaCurrency != null ? saaCurrency : analyticsCurrency;
                      return (
                        <td key={p.key} className="weight-col">
                          {currencyVal != null ? `${(currencyVal * 100).toFixed(1)}%` : <span style={{ color: 'var(--gray-color)' }}>-</span>}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Sub Asset Class View */}
          {holdingsView === 'sac' && (
            <div style={{ overflowX: 'auto' }}>
              <table className="holdings-table">
                <thead>
                  <tr>
                    <th className="name-col" colSpan={2}>Sub Asset Class</th>
                    {comparisonTable.portfolios.map(p => {
                      const geared = comparisonTable.rows.some(row => (row.weights[p.key] || 0) < 0);
                      return (
                        <th key={p.key} className="weight-col" style={{ borderBottom: `3px solid ${p.colour}` }}>
                          <div>{p.name}</div>
                          <div style={{ fontSize: '0.65rem', fontWeight: 400, opacity: 0.8 }}>
                            {p.type === 'custom' ? 'Custom' : p.type === 'saa' ? 'SAA' : 'Model'}
                            {geared && (
                              <span
                                className="negative-chip"
                                title="Geared portfolio — holds negative (borrowed/short) weights, nets to 100%"
                              >GEARED</span>
                            )}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Collect all SACs across all portfolios
                    const allSacs = new Set();
                    comparisonTable.portfolios.forEach(p => {
                      const sw = analyticsData?.portfolios?.[p.key]?.sac_weights;
                      if (sw) Object.keys(sw).forEach(k => { if (k !== 'CURRENCY') allSacs.add(k); });
                    });
                    const sacList = [...allSacs].sort();
                    return sacList.map((sac, i) => (
                      <tr key={sac} className="holding-row">
                        <td className="name-col" colSpan={2}>{sac}</td>
                        {comparisonTable.portfolios.map(p => {
                          const val = analyticsData?.portfolios?.[p.key]?.sac_weights?.[sac];
                          return (
                            <td key={p.key} className="weight-col">
                              {val != null
                                ? `${(val * 100).toFixed(1)}%`
                                : <span style={{ color: 'var(--gray-color)' }}>-</span>
                              }
                            </td>
                          );
                        })}
                      </tr>
                    ));
                  })()}
                  <tr className="total-row">
                    <td colSpan={2}>Total</td>
                    {comparisonTable.portfolios.map(p => {
                      const sw = analyticsData?.portfolios?.[p.key]?.sac_weights;
                      const total = sw ? Object.entries(sw).reduce((sum, [k, v]) => k !== 'CURRENCY' ? sum + v : sum, 0) : 0;
                      return <td key={p.key} className="weight-col">{(total * 100).toFixed(1)}%</td>;
                    })}
                  </tr>
                  <tr className="total-row" style={{ borderTop: '2px solid var(--border-color)' }}>
                    <td colSpan={2}>Growth Exposure</td>
                    {comparisonTable.portfolios.map(p => {
                      // Use the backend-computed value (taxonomy-aware via
                      // SubAssetClassConfig.growth_exposure); the previous
                      // hardcoded SAC list didn't know about the private-market
                      // SACs (GL_PE, GL_PRIVATE_*, AU_PRIVATE_*).
                      const growthVal = analyticsData?.portfolios?.[p.key]?.growth_exposure ?? null;
                      return (
                        <td key={p.key} className="weight-col">
                          {growthVal != null ? `${(growthVal * 100).toFixed(1)}%` : <span style={{ color: 'var(--gray-color)' }}>-</span>}
                        </td>
                      );
                    })}
                  </tr>
                  <tr className="total-row">
                    <td colSpan={2}>Currency Exposure</td>
                    {comparisonTable.portfolios.map(p => {
                      const saaCurrency = p.currency_exposure;
                      const analyticsCurrency = analyticsData?.portfolios?.[p.key]?.sac_weights?.CURRENCY;
                      const currencyVal = saaCurrency != null ? saaCurrency : analyticsCurrency;
                      return (
                        <td key={p.key} className="weight-col">
                          {currencyVal != null ? `${(currencyVal * 100).toFixed(1)}%` : <span style={{ color: 'var(--gray-color)' }}>-</span>}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Asset Class Group View */}
          {holdingsView === 'assetClass' && (
            <div style={{ overflowX: 'auto' }}>
              <table className="holdings-table">
                <thead>
                  <tr>
                    <th className="name-col" colSpan={2}>Asset Class Group</th>
                    {comparisonTable.portfolios.map(p => {
                      const geared = comparisonTable.rows.some(row => (row.weights[p.key] || 0) < 0);
                      return (
                        <th key={p.key} className="weight-col" style={{ borderBottom: `3px solid ${p.colour}` }}>
                          <div>{p.name}</div>
                          <div style={{ fontSize: '0.65rem', fontWeight: 400, opacity: 0.8 }}>
                            {p.type === 'custom' ? 'Custom' : p.type === 'saa' ? 'SAA' : 'Model'}
                            {geared && (
                              <span
                                className="negative-chip"
                                title="Geared portfolio — holds negative (borrowed/short) weights, nets to 100%"
                              >GEARED</span>
                            )}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Use lookthrough data from analytics (asset class group weights)
                    const allClasses = new Set();
                    comparisonTable.portfolios.forEach(p => {
                      const lt = analyticsData?.portfolios?.[p.key]?.lookthrough;
                      if (lt) Object.keys(lt).forEach(k => allClasses.add(k));
                    });
                    const classList = [...allClasses].sort();
                    return classList.map((ac, i) => {
                      const hasValue = comparisonTable.portfolios.some(p => {
                        const val = analyticsData?.portfolios?.[p.key]?.lookthrough?.[ac];
                        return val != null && val > 0.0005;
                      });
                      if (!hasValue) return null;
                      return (
                        <tr key={ac} className="holding-row">
                          <td className="name-col" colSpan={2}>
                            <span
                              className="asset-class-indicator"
                              style={{ backgroundColor: getAssetClassColor(ac, i) }}
                            />
                            {ac}
                          </td>
                          {comparisonTable.portfolios.map(p => {
                            const val = analyticsData?.portfolios?.[p.key]?.lookthrough?.[ac];
                            return (
                              <td key={p.key} className="weight-col">
                                {val != null && val > 0.0005
                                  ? `${(val * 100).toFixed(1)}%`
                                  : <span style={{ color: 'var(--gray-color)' }}>-</span>
                                }
                              </td>
                            );
                          })}
                        </tr>
                      );
                    });
                  })()}
                  <tr className="total-row">
                    <td colSpan={2}>Total</td>
                    {comparisonTable.portfolios.map(p => {
                      const lt = analyticsData?.portfolios?.[p.key]?.lookthrough;
                      const total = lt ? Object.values(lt).reduce((sum, v) => sum + v, 0) : 0;
                      return <td key={p.key} className="weight-col">{(total * 100).toFixed(1)}%</td>;
                    })}
                  </tr>
                  <tr className="total-row" style={{ borderTop: '2px solid var(--border-color)' }}>
                    <td colSpan={2}>Growth Exposure</td>
                    {comparisonTable.portfolios.map(p => {
                      // Use the backend-computed value (taxonomy-aware via
                      // SubAssetClassConfig.growth_exposure); the previous
                      // hardcoded SAC list didn't know about the private-market
                      // SACs (GL_PE, GL_PRIVATE_*, AU_PRIVATE_*).
                      const growthVal = analyticsData?.portfolios?.[p.key]?.growth_exposure ?? null;
                      return (
                        <td key={p.key} className="weight-col">
                          {growthVal != null ? `${(growthVal * 100).toFixed(1)}%` : <span style={{ color: 'var(--gray-color)' }}>-</span>}
                        </td>
                      );
                    })}
                  </tr>
                  <tr className="total-row">
                    <td colSpan={2}>Currency Exposure</td>
                    {comparisonTable.portfolios.map(p => {
                      const saaCurrency = p.currency_exposure;
                      const analyticsCurrency = analyticsData?.portfolios?.[p.key]?.sac_weights?.CURRENCY;
                      const currencyVal = saaCurrency != null ? saaCurrency : analyticsCurrency;
                      return (
                        <td key={p.key} className="weight-col">
                          {currencyVal != null ? `${(currencyVal * 100).toFixed(1)}%` : <span style={{ color: 'var(--gray-color)' }}>-</span>}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Summary: number of overlapping funds */}
          <div className="mt-3 p-3" style={{ background: 'rgba(89, 155, 227, 0.08)', borderRadius: '6px', fontSize: '0.85rem' }}>
            <strong>Summary:</strong>{' '}
            {comparisonTable.rows.length} unique fund{comparisonTable.rows.length !== 1 ? 's' : ''} across{' '}
            {comparisonTable.portfolios.length} portfolios.{' '}
            {(() => {
              const overlap = comparisonTable.rows.filter(row =>
                comparisonTable.portfolios.every(p => row.weights[p.key] != null)
              ).length;
              return overlap > 0
                ? `${overlap} fund${overlap !== 1 ? 's' : ''} held in common.`
                : 'No funds held in common.';
            })()}
          </div>

        </div>
      )}

      {/* Factor Exposures */}
      {comparisonTable && compareSection === 'factors' && (
        <div className="factor-exposures-section">
            {loadingFactors && (
              <div className="d-flex align-items-center gap-2 mb-3" style={{ fontSize: '0.85rem' }}>
                <div className="spinner-border spinner-border-sm text-primary" role="status" />
                <span className="text-muted">Loading factor exposures...</span>
              </div>
            )}

            {factorData && !loadingFactors && (
              <>
                {/* View toggle */}
                <div className="d-flex justify-content-end mb-3">
                  <div className="btn-group">
                    <button
                      className={`btn btn-sm ${factorViewMode === 'table' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      style={factorViewMode === 'table' ? { background: 'var(--primary-color)', borderColor: 'var(--primary-color)' } : {}}
                      onClick={() => setFactorViewMode('table')}
                    >
                      Table
                    </button>
                    <button
                      className={`btn btn-sm ${factorViewMode === 'chart' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      style={factorViewMode === 'chart' ? { background: 'var(--primary-color)', borderColor: 'var(--primary-color)' } : {}}
                      onClick={() => setFactorViewMode('chart')}
                    >
                      Chart
                    </button>
                  </div>
                </div>

                {/* Match info per portfolio */}
                <div className="match-info mb-2">
                  {comparisonTable.portfolios.map(p => {
                    const pd = factorData.portfolios?.[p.key];
                    if (!pd) return null;
                    return (
                      <span key={p.key} style={{ marginRight: '1.5rem' }}>
                        <strong>{p.name}:</strong> {pd.matched_count}/{pd.total_count} funds matched
                      </span>
                    );
                  })}
                </div>

                {Object.keys(factorData.sac_factors || {}).length === 0 ? (
                  <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                    No factor data available for these portfolios.
                  </div>
                ) : (
                  Object.entries(factorData.sac_factors).map(([sac, factors]) => {
                    const benchmarkTotals = benchmarkKey ? factorData.portfolios?.[benchmarkKey]?.sac_totals?.[sac] : null;
                    const isExpanded = expandedSacs.has(sac);

                    // Helper to render a diff cell next to its portfolio cell
                    const renderDiff = (pKey, val, bVal, isPercent) => {
                      if (!benchmarkKey || pKey === benchmarkKey) return null;
                      if (val == null || bVal == null) return <td key={`${pKey}-diff`} className="diff-col">-</td>;
                      const diff = isPercent ? (val - bVal) * 100 : val - bVal;
                      const formatted = isPercent ? `${diff > 0 ? '+' : ''}${diff.toFixed(1)}%` : `${diff > 0 ? '+' : ''}${diff.toFixed(2)}`;
                      return (
                        <td key={`${pKey}-diff`} className={`diff-col ${diff > 0 ? 'diff-positive' : diff < 0 ? 'diff-negative' : ''}`}>
                          {formatted}
                        </td>
                      );
                    };

                    return (
                      <div key={sac} className="sac-block">
                        <div className="sac-header">
                          <span>{sac} — {sacDisplayNames[sac] || sac}</span>
                          <button
                            className="detail-toggle"
                            onClick={() => toggleSacExpanded(sac)}
                          >
                            {isExpanded ? 'Hide fund details \u25B2' : 'Show fund details \u25BC'}
                          </button>
                        </div>

                        {factorViewMode === 'table' ? (
                          /* ── TABLE VIEW ── */
                          <div style={{ overflowX: 'auto' }}>
                            <table className="factor-summary-table">
                              <thead>
                                <tr>
                                  <th></th>
                                  {comparisonTable.portfolios.map(p => (
                                    <React.Fragment key={p.key}>
                                      <th style={{ borderBottom: `3px solid ${p.colour}` }}>{p.name}</th>
                                      {benchmarkKey && p.key !== benchmarkKey && (
                                        <th className="diff-col" style={{ borderBottom: `3px solid ${p.colour}` }}>{'\u0394'}</th>
                                      )}
                                    </React.Fragment>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {/* Weight row */}
                                <tr>
                                  <td>Weight %</td>
                                  {comparisonTable.portfolios.map(p => {
                                    const t = factorData.portfolios?.[p.key]?.sac_totals?.[sac];
                                    const val = t?.weight;
                                    return (
                                      <React.Fragment key={p.key}>
                                        <td>{val != null ? `${val.toFixed(1)}%` : '-'}</td>
                                        {benchmarkKey && p.key !== benchmarkKey && (() => {
                                          const bVal = benchmarkTotals?.weight;
                                          if (val == null || bVal == null) return <td className="diff-col">-</td>;
                                          const diff = val - bVal;
                                          return (
                                            <td className={`diff-col ${diff > 0 ? 'diff-positive' : diff < 0 ? 'diff-negative' : ''}`}>
                                              {diff > 0 ? '+' : ''}{diff.toFixed(1)}
                                            </td>
                                          );
                                        })()}
                                      </React.Fragment>
                                    );
                                  })}
                                </tr>

                                {/* R² row */}
                                <tr>
                                  <td>R²</td>
                                  {comparisonTable.portfolios.map(p => {
                                    const t = factorData.portfolios?.[p.key]?.sac_totals?.[sac];
                                    const val = t?.r_squared;
                                    return (
                                      <React.Fragment key={p.key}>
                                        <td>{val != null ? val.toFixed(2) : '-'}</td>
                                        {renderDiff(p.key, val, benchmarkTotals?.r_squared, false)}
                                      </React.Fragment>
                                    );
                                  })}
                                </tr>

                                {/* Beta rows */}
                                {factors.map(factor => (
                                  <tr key={factor}>
                                    <td>{abbreviateFactorName(factor)}</td>
                                    {comparisonTable.portfolios.map(p => {
                                      const t = factorData.portfolios?.[p.key]?.sac_totals?.[sac];
                                      const val = t?.betas?.[factor];
                                      return (
                                        <React.Fragment key={p.key}>
                                          <td style={{ background: getBetaColor(val) }}>
                                            {val != null ? val.toFixed(2) : '-'}
                                          </td>
                                          {renderDiff(p.key, val, benchmarkTotals?.betas?.[factor], false)}
                                        </React.Fragment>
                                      );
                                    })}
                                  </tr>
                                ))}

                                {/* Risk rows */}
                                {[
                                  { key: 'specific_risk_annual', label: 'Spec Risk' },
                                  { key: 'factor_risk_annual', label: 'Factor Risk' },
                                  { key: 'total_risk_annual', label: 'Total Risk' },
                                ].map(({ key, label }) => (
                                  <tr key={key}>
                                    <td>{label}</td>
                                    {comparisonTable.portfolios.map(p => {
                                      const t = factorData.portfolios?.[p.key]?.sac_totals?.[sac];
                                      const val = t?.[key];
                                      return (
                                        <React.Fragment key={p.key}>
                                          <td>{val != null ? `${(val * 100).toFixed(1)}%` : '-'}</td>
                                          {renderDiff(p.key, val, benchmarkTotals?.[key], true)}
                                        </React.Fragment>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          /* ── CHART VIEW ── */
                          <div className="chart-grid">
                            <div className="chart-cell">
                              <Bar
                                data={{
                                  labels: factors.map(abbreviateFactorName),
                                  datasets: comparisonTable.portfolios.map(p => ({
                                    label: p.name,
                                    data: factors.map(f => factorData.portfolios?.[p.key]?.sac_totals?.[sac]?.betas?.[f] ?? null),
                                    backgroundColor: p.colour,
                                    borderColor: p.colour,
                                    borderWidth: 1,
                                  })),
                                }}
                                options={{
                                  responsive: true,
                                  maintainAspectRatio: true,
                                  aspectRatio: 1.6,
                                  plugins: {
                                    title: { display: true, text: `Factor Betas`, color: '#072040', font: { size: 11, weight: 'bold' }, padding: { bottom: 4 } },
                                    legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 6, font: { size: 9 }, padding: 8 } },
                                    tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y?.toFixed(3) ?? '-'}` } },
                                  },
                                  scales: {
                                    x: { grid: { display: false }, ticks: { font: { size: 9 } } },
                                    y: { title: { display: true, text: 'Beta', font: { size: 9 } }, grid: { color: 'rgba(0,0,0,0.06)' }, ticks: { font: { size: 8 } } },
                                  },
                                }}
                              />
                            </div>
                            <div className="chart-cell">
                              <Bar
                                data={{
                                  labels: comparisonTable.portfolios.map(p => wrapLabel(p.name)),
                                  datasets: [
                                    {
                                      label: 'Specific Risk',
                                      data: comparisonTable.portfolios.map(p => {
                                        const v = factorData.portfolios?.[p.key]?.sac_totals?.[sac]?.specific_risk_annual;
                                        return v != null ? +(v * 100).toFixed(2) : null;
                                      }),
                                      backgroundColor: '#f0a500',
                                    },
                                    {
                                      label: 'Factor Risk',
                                      data: comparisonTable.portfolios.map(p => {
                                        const v = factorData.portfolios?.[p.key]?.sac_totals?.[sac]?.factor_risk_annual;
                                        return v != null ? +(v * 100).toFixed(2) : null;
                                      }),
                                      backgroundColor: '#599BE3',
                                    },
                                    {
                                      label: 'Total Risk',
                                      data: comparisonTable.portfolios.map(p => {
                                        const v = factorData.portfolios?.[p.key]?.sac_totals?.[sac]?.total_risk_annual;
                                        return v != null ? +(v * 100).toFixed(2) : null;
                                      }),
                                      backgroundColor: '#072040',
                                    },
                                  ],
                                }}
                                options={{
                                  responsive: true,
                                  maintainAspectRatio: true,
                                  aspectRatio: 1.6,
                                  plugins: {
                                    title: { display: true, text: `Risk Decomposition`, color: '#072040', font: { size: 11, weight: 'bold' }, padding: { bottom: 4 } },
                                    legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 6, font: { size: 9 }, padding: 8 } },
                                    tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y?.toFixed(1) ?? '-'}%` } },
                                  },
                                  scales: {
                                    x: { grid: { display: false }, ticks: { font: { size: 9 } } },
                                    y: { title: { display: true, text: 'Risk (%)', font: { size: 9 } }, grid: { color: 'rgba(0,0,0,0.06)' }, ticks: { font: { size: 8 }, callback: (v) => `${v}%` } },
                                  },
                                }}
                              />
                            </div>
                          </div>
                        )}

                        {/* Expandable fund-level detail */}
                        {isExpanded && (
                          <div className="fund-detail-section">
                            {comparisonTable.portfolios.map(p => {
                              const funds = factorData.portfolios?.[p.key]?.sac_groups?.[sac];
                              const totals = factorData.portfolios?.[p.key]?.sac_totals?.[sac];
                              if (!funds || funds.length === 0) return null;

                              const fundLabels = funds.map(f => {
                                const name = f.fund_name || f.tr_code;
                                return name.length > 20 ? name.substring(0, 18) + '...' : name;
                              });

                              return (
                                <div key={p.key}>
                                  <div className="portfolio-sub-header" style={{ borderBottomColor: p.colour }}>
                                    {p.name}
                                  </div>

                                  {factorViewMode === 'table' ? (
                                    <div style={{ overflowX: 'auto' }}>
                                      <table className="fund-detail-table">
                                        <thead>
                                          <tr>
                                            <th>Fund</th>
                                            <th>Wt %</th>
                                            <th>R²</th>
                                            {factors.map(f => (
                                              <th key={f} title={f}>{abbreviateFactorName(f)}</th>
                                            ))}
                                            <th>Spec Risk</th>
                                            <th>Factor Risk</th>
                                            <th>Total Risk</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {funds.map(fund => (
                                            <tr key={fund.tr_code}>
                                              <td title={fund.tr_code}>{fund.fund_name || fund.tr_code}</td>
                                              <td>{fund.weight?.toFixed(1)}</td>
                                              <td>{fund.r_squared != null ? fund.r_squared.toFixed(2) : '-'}</td>
                                              {factors.map(f => {
                                                const val = fund.betas?.[f];
                                                return (
                                                  <td key={f} style={{ background: getBetaColor(val) }}>
                                                    {val != null ? val.toFixed(2) : '-'}
                                                  </td>
                                                );
                                              })}
                                              <td>{fund.specific_risk_annual != null ? `${(fund.specific_risk_annual * 100).toFixed(1)}%` : '-'}</td>
                                              <td>{fund.factor_risk_annual != null ? `${(fund.factor_risk_annual * 100).toFixed(1)}%` : '-'}</td>
                                              <td>{fund.total_risk_annual != null ? `${(fund.total_risk_annual * 100).toFixed(1)}%` : '-'}</td>
                                            </tr>
                                          ))}
                                          {totals && (
                                            <tr className="total-row">
                                              <td>Portfolio Total</td>
                                              <td>{totals.weight?.toFixed(1)}</td>
                                              <td>{totals.r_squared?.toFixed(2)}</td>
                                              {factors.map(f => {
                                                const val = totals.betas?.[f];
                                                return (
                                                  <td key={f} style={{ background: getBetaColor(val) }}>
                                                    {val != null ? val.toFixed(2) : '-'}
                                                  </td>
                                                );
                                              })}
                                              <td>{totals.specific_risk_annual != null ? `${(totals.specific_risk_annual * 100).toFixed(1)}%` : '-'}</td>
                                              <td>{totals.factor_risk_annual != null ? `${(totals.factor_risk_annual * 100).toFixed(1)}%` : '-'}</td>
                                              <td>{totals.total_risk_annual != null ? `${(totals.total_risk_annual * 100).toFixed(1)}%` : '-'}</td>
                                            </tr>
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  ) : (
                                    <div className="chart-grid">
                                      <div className="chart-cell">
                                        <Bar
                                          data={{
                                            labels: fundLabels,
                                            datasets: factors.map((f, fi) => ({
                                              label: abbreviateFactorName(f),
                                              data: funds.map(fund => fund.betas?.[f] ?? null),
                                              backgroundColor: COMPARISON_COLOURS[fi % COMPARISON_COLOURS.length],
                                            })),
                                          }}
                                          options={{
                                            responsive: true,
                                            maintainAspectRatio: true,
                                            aspectRatio: 1.4,
                                            plugins: {
                                              title: { display: true, text: `Factor Betas by Fund`, color: '#072040', font: { size: 11, weight: 'bold' }, padding: { bottom: 4 } },
                                              legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 6, font: { size: 9 }, padding: 8 } },
                                              tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y?.toFixed(3) ?? '-'}` } },
                                            },
                                            scales: {
                                              x: { grid: { display: false }, ticks: { font: { size: 8 }, maxRotation: 45, minRotation: 0 } },
                                              y: { title: { display: true, text: 'Beta', font: { size: 9 } }, grid: { color: 'rgba(0,0,0,0.06)' }, ticks: { font: { size: 8 } } },
                                            },
                                          }}
                                        />
                                      </div>
                                      <div className="chart-cell">
                                        <Bar
                                          data={{
                                            labels: fundLabels,
                                            datasets: [
                                              {
                                                label: 'Specific Risk',
                                                data: funds.map(f => f.specific_risk_annual != null ? +(f.specific_risk_annual * 100).toFixed(2) : null),
                                                backgroundColor: '#f0a500',
                                              },
                                              {
                                                label: 'Factor Risk',
                                                data: funds.map(f => f.factor_risk_annual != null ? +(f.factor_risk_annual * 100).toFixed(2) : null),
                                                backgroundColor: '#599BE3',
                                              },
                                              {
                                                label: 'Total Risk',
                                                data: funds.map(f => f.total_risk_annual != null ? +(f.total_risk_annual * 100).toFixed(2) : null),
                                                backgroundColor: '#072040',
                                              },
                                            ],
                                          }}
                                          options={{
                                            responsive: true,
                                            maintainAspectRatio: true,
                                            aspectRatio: 1.4,
                                            plugins: {
                                              title: { display: true, text: `Risk Decomposition by Fund`, color: '#072040', font: { size: 11, weight: 'bold' }, padding: { bottom: 4 } },
                                              legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 6, font: { size: 9 }, padding: 8 } },
                                              tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y?.toFixed(1) ?? '-'}%` } },
                                            },
                                            scales: {
                                              x: { grid: { display: false }, ticks: { font: { size: 8 }, maxRotation: 45, minRotation: 0 } },
                                              y: { title: { display: true, text: 'Risk (%)', font: { size: 9 } }, grid: { color: 'rgba(0,0,0,0.06)' }, ticks: { font: { size: 8 }, callback: (v) => `${v}%` } },
                                            },
                                          }}
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}

                {/* Excluded funds footnote */}
                {(() => {
                  const allExcluded = comparisonTable.portfolios.flatMap(p => {
                    const ef = factorData.portfolios?.[p.key]?.excluded_funds;
                    if (!ef || ef.length === 0) return [];
                    return ef.map(f => ({ ...f, portfolio: p.name }));
                  });
                  if (allExcluded.length === 0) return null;
                  return (
                    <div style={{ marginTop: '0.75rem', fontSize: '0.72rem', color: 'var(--gray-color)', borderTop: '1px solid var(--light-color)', paddingTop: '0.5rem' }}>
                      <strong>Excluded funds (no factor loadings data):</strong>{' '}
                      {allExcluded.map((f, i) => (
                        <span key={i}>{f.fund_name || f.fund_code}{i < allExcluded.length - 1 ? ', ' : ''}</span>
                      ))}
                    </div>
                  );
                })()}
              </>
            )}
          </div>
      )}

      {/* Portfolio Analytics */}
      {comparisonTable && compareSection === 'analytics' && (
        <div className="analytics-section">
            {loadingAnalytics && (
              <div className="d-flex align-items-center gap-2 mb-3" style={{ fontSize: '0.85rem' }}>
                <div className="spinner-border spinner-border-sm text-primary" role="status" />
                <span className="text-muted">Computing portfolio analytics (this may take a moment)...</span>
              </div>
            )}

            {analyticsData && !loadingAnalytics && (
              <>
                {/* View toggle */}
                <div className="d-flex justify-content-end mb-2">
                  <div className="btn-group">
                    <button
                      className={`btn btn-sm ${analyticsViewMode === 'table' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      style={analyticsViewMode === 'table' ? { background: 'var(--primary-color)', borderColor: 'var(--primary-color)' } : {}}
                      onClick={() => setAnalyticsViewMode('table')}
                    >
                      Table
                    </button>
                    <button
                      className={`btn btn-sm ${analyticsViewMode === 'chart' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      style={analyticsViewMode === 'chart' ? { background: 'var(--primary-color)', borderColor: 'var(--primary-color)' } : {}}
                      onClick={() => setAnalyticsViewMode('chart')}
                    >
                      Chart
                    </button>
                  </div>
                </div>

                {/* Match info */}
                <div className="match-info mb-2">
                  {comparisonTable.portfolios.map(p => {
                    const ad = analyticsData.portfolios?.[p.key];
                    if (!ad) return null;
                    return (
                      <span key={p.key} style={{ marginRight: '1.5rem' }}>
                        <strong>{p.name}:</strong> {ad.matched_count}/{ad.total_count} funds matched
                      </span>
                    );
                  })}
                </div>

                {/* Excluded funds footnote */}
                {(() => {
                  const allExcluded = comparisonTable.portfolios.flatMap(p => {
                    const ef = analyticsData.portfolios?.[p.key]?.excluded_funds;
                    if (!ef || ef.length === 0) return [];
                    return ef.map(f => ({ ...f, portfolio: p.name }));
                  });
                  if (allExcluded.length === 0) return null;
                  // Group by reason
                  const byReason = {};
                  allExcluded.forEach(f => {
                    if (!byReason[f.reason]) byReason[f.reason] = [];
                    byReason[f.reason].push(f);
                  });
                  return (
                    <div style={{ marginBottom: '0.75rem', fontSize: '0.72rem', color: 'var(--gray-color)', borderLeft: '2px solid var(--secondary-color)', paddingLeft: '0.5rem' }}>
                      {Object.entries(byReason).map(([reason, funds]) => (
                        <div key={reason}>
                          <strong>{reason}:</strong>{' '}
                          {funds.map((f, i) => (
                            <span key={i}>{f.fund_name || f.fund_code}{i < funds.length - 1 ? ', ' : ''}</span>
                          ))}
                        </div>
                      ))}
                    </div>
                  );
                })()}

                {Object.keys(analyticsData.portfolios || {}).length === 0 ? (
                  <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                    No analytics data available for these portfolios.
                  </div>
                ) : (
                  <>
                    {/* ── Summary Metrics Table ── */}
                    {analyticsViewMode === 'table' ? (
                      <div style={{ overflowX: 'auto', marginBottom: '1.5rem' }}>
                        <table className="factor-summary-table">
                          <thead>
                            <tr>
                              <th>Metric</th>
                              {comparisonTable.portfolios.map(p => {
                                const ad = analyticsData.portfolios?.[p.key];
                                if (!ad) return null;
                                return (
                                  <React.Fragment key={p.key}>
                                    <th style={{ borderBottom: `3px solid ${p.colour}` }}>{p.name}</th>
                                    {benchmarkKey && p.key !== benchmarkKey && (
                                      <th className="diff-col" style={{ borderBottom: `3px solid ${p.colour}` }}>{'\u0394'}</th>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              const bData = benchmarkKey ? analyticsData.portfolios?.[benchmarkKey] : null;
                              const bMetrics = bData?.stochastics?.metrics;

                              // Return objective rows — only shown when at least one
                              // portfolio in the comparison has an objective set.
                              const hasObjective = comparisonTable.portfolios.some(
                                p => analyticsData.portfolios?.[p.key]?.objective
                              );
                              const objectiveMetrics = hasObjective ? [
                                { label: 'Objective', get: (d) => d?.objective?.objective_label ?? null, fmt: (v) => v, noDiff: true,
                                  tooltip: 'The portfolio\'s return objective as configured in the portfolio details. CPI+ objectives add the assumed CPI (2.5%), Cash+ objectives add the CMA cash expected return, and Absolute objectives stand alone.' },
                                { label: 'Objective Rate', get: (d) => d?.objective?.objective_rate ?? null, fmt: (v) => `${(v * 100).toFixed(2)}%`, pct: true,
                                  tooltip: 'The objective expressed as an annualised nominal rate: base rate (CPI 2.5%, CMA cash return, or 0 for absolute) plus the return objective.' },
                                { label: 'Exp. vs Objective', get: (d) => d?.objective?.excess_vs_expected ?? null, fmt: (v) => `${v > 0 ? '+' : ''}${(v * 100).toFixed(2)}%`, pct: true,
                                  tooltip: 'CMA expected return minus the objective rate. Positive means the portfolio\'s expected return exceeds its objective.' },
                                { label: 'P(Meet Objective)', get: (d) => d?.objective?.objective_probability ?? null, fmt: (v) => `${(v * 100).toFixed(0)}%`, pct: true,
                                  tooltip: 'Monte-Carlo probability that terminal wealth over the objective\'s time horizon exceeds the objective rate compounded over that horizon. Requires a time horizon to be set. Uses the same 10,000-path stochastic simulation as the other risk metrics.' },
                              ] : [];

                              const metrics = [
                                { label: 'Expected Return', get: (d) => d?.expected_return, fmt: (v) => `${(v * 100).toFixed(2)}%`, pct: true,
                                  tooltip: 'Annualised expected return derived from the capital market assumptions (CMAs) for each sub-asset class. Calculated as the weighted sum of each sub-asset class\'s expected annual return, where weights are the portfolio\'s allocation to each sub-asset class. Uses the covariance-adjusted return/volatility framework from the blender.' },
                                { label: 'Volatility', get: (d) => d?.volatility, fmt: (v) => `${(v * 100).toFixed(2)}%`, pct: true,
                                  tooltip: 'Annualised portfolio volatility (standard deviation of returns). Calculated using the full covariance matrix: σ = √(w\'Σw), where w is the vector of sub-asset class weights and Σ is the covariance matrix derived from correlation and individual asset volatilities. Accounts for diversification benefits between asset classes.' },
                                { label: 'Growth Exposure', get: (d) => d?.growth_exposure ?? null,
                                  fmt: (v) => `${(v * 100).toFixed(1)}%`, pct: true,
                                  tooltip: 'Weighted sum of each sub-asset class\'s configured growth exposure (set in Investment Config → Sub Asset Classes). Growth SACs include listed equities, hedge funds, infrastructure, REITs, and private equity; defensive SACs include bonds, credit, cash, and private debt.' },
                                { label: 'Income Yield', get: (d) => d?.income, fmt: (v) => `${(v * 100).toFixed(1)}%`, pct: true,
                                  tooltip: 'Estimated annual income yield of the portfolio: the weighted average of the income rate of each sub-asset class (dividends, coupons, distributions), as set on the investment-config Sub Asset Classes tab. Classes ticked "Income = Return" use the default CMA scenario expected return as their income.' },
                                ...objectiveMetrics,
                                { label: 'Return/Risk Ratio', get: (d) => d?.stochastics?.metrics?.return_risk_ratio, fmt: (v) => v.toFixed(3),
                                  tooltip: 'Expected Return divided by Volatility. Measures the return earned per unit of risk taken. Higher values indicate more efficient portfolios. Derived from the stochastic simulation — calculated as the median annualised return across all simulated paths divided by the standard deviation of those returns.' },
                                { label: 'Sortino Ratio', get: (d) => d?.stochastics?.metrics?.sortino_ratio, fmt: (v) => v.toFixed(3),
                                  tooltip: 'Similar to Return/Risk Ratio but only penalises downside volatility. Calculated as Expected Return / Downside Deviation, where downside deviation measures only the volatility of negative returns from the stochastic simulation. Higher values indicate better risk-adjusted returns, with a focus on harmful volatility rather than total volatility.' },
                                { label: 'Calmar Ratio', get: (d) => d?.stochastics?.metrics?.calmar_ratio, fmt: (v) => v.toFixed(3),
                                  tooltip: 'Expected Return divided by the Average Maximum Drawdown from the stochastic simulation. Measures return earned relative to the worst peak-to-trough decline experienced. Higher values indicate the portfolio recovers more return for each unit of drawdown risk. Particularly useful for assessing tail risk tolerance.' },
                                { label: 'VaR 95%', get: (d) => d?.stochastics?.metrics?.var_95, fmt: (v) => `${(v * 100).toFixed(2)}%`, pct: true,
                                  tooltip: 'Value at Risk at 95% confidence level. The 5th percentile of annual returns from the stochastic simulation — meaning there is a 5% probability of the portfolio losing more than this amount in any given year. For example, VaR 95% of -9.26% means the portfolio is expected to lose more than 9.26% only 1 in 20 years.' },
                                { label: 'CVaR 95%', get: (d) => d?.stochastics?.metrics?.cvar_95, fmt: (v) => `${(v * 100).toFixed(2)}%`, pct: true,
                                  tooltip: 'Conditional Value at Risk (Expected Shortfall) at 95% confidence. The average loss in the worst 5% of simulated annual outcomes — i.e. the expected loss given that you are already in the tail. Always worse than VaR as it captures the severity of extreme losses, not just their threshold. Critical for understanding tail risk.' },
                                { label: 'Avg Max Drawdown', get: (d) => d?.stochastics?.metrics?.avg_max_drawdown, fmt: (v) => `${(v * 100).toFixed(2)}%`, pct: true,
                                  tooltip: 'The average of the maximum peak-to-trough drawdown across all stochastic simulation paths (30-year horizon). Represents the typical worst decline an investor might experience over the life of the portfolio. Accounts for the compounding path of returns, not just single-year losses.' },
                                { label: 'Worst Max Drawdown', get: (d) => d?.stochastics?.metrics?.worst_max_drawdown, fmt: (v) => `${(v * 100).toFixed(2)}%`, pct: true,
                                  tooltip: 'The single worst peak-to-trough drawdown observed across all 10,000 stochastic simulation paths — a roughly 1-in-10,000 outcome and therefore extremely unlikely. Represents the most extreme tail observation rather than a typical or expected decline.' },
                                { label: 'Worst 1-Year Return', get: (d) => d?.stochastics?.metrics?.worst_1yr, fmt: (v) => `${(v * 100).toFixed(2)}%`, pct: true,
                                  tooltip: 'The worst single-year return observed across all stochastic simulation paths. Unlike VaR which is a percentile threshold, this is the absolute worst outcome in any single year across all simulations. Useful for stress-testing client expectations around worst-case annual performance.' },
                                { label: 'Win Rate', get: (d) => d?.stochastics?.metrics?.win_rate, fmt: (v) => `${(v * 100).toFixed(1)}%`, pct: true,
                                  tooltip: 'Percentage of simulated years that produced a positive return. Calculated across all years in all stochastic simulation paths. A win rate of 72% means the portfolio is expected to deliver a positive annual return in approximately 72 out of every 100 years.' },
                                { label: 'Expected Neg. Years (20yr)', get: (d) => d?.stochastics?.metrics?.expected_negative_years, fmt: (v) => v.toFixed(1),
                                  tooltip: 'Expected number of negative return years over a 20-year investment horizon. Derived from the stochastic simulation win rate: (1 - Win Rate) × 20. For example, a win rate of 72% implies approximately 5.6 negative years out of 20. Used as the primary input for the SRM Risk Band classification.' },
                                { label: 'SRM Risk Band', get: (d) => d?.srm_band, fmt: (v, d) => `${v} — ${d?.srm_label || ''}`,
                                  tooltip: 'Standard Risk Measure band (1-7) as defined by ASFA/FSC. Based on the expected number of negative annual returns over a 20-year period. Band 1 (Very Low): <0.5 years, Band 2 (Low): 0.5-1, Band 3 (Low to Medium): 1-2, Band 4 (Medium): 2-3, Band 5 (Medium to High): 3-4, Band 6 (High): 4-6, Band 7 (Very High): 6+. Required disclosure for Australian managed investment products.' },
                              ];

                              return metrics.map(m => (
                                <tr key={m.label}>
                                  <td style={{ cursor: m.tooltip ? 'help' : 'default' }} title={m.tooltip || ''}>{m.label}</td>
                                  {comparisonTable.portfolios.map(p => {
                                    const ad = analyticsData.portfolios?.[p.key];
                                    if (!ad) return null;
                                    const val = m.get(ad);
                                    const bVal = bData ? m.get(bData) : null;
                                    return (
                                      <React.Fragment key={p.key}>
                                        <td>{val != null ? m.fmt(val, ad) : '-'}</td>
                                        {benchmarkKey && p.key !== benchmarkKey && (() => {
                                          if (val == null || bVal == null || m.label === 'SRM Risk Band' || m.noDiff) return <td className="diff-col">-</td>;
                                          const diff = m.pct ? (val - bVal) * 100 : val - bVal;
                                          const formatted = m.pct ? `${diff > 0 ? '+' : ''}${diff.toFixed(2)}%` : `${diff > 0 ? '+' : ''}${diff.toFixed(3)}`;
                                          return (
                                            <td className={`diff-col ${diff > 0 ? 'diff-positive' : diff < 0 ? 'diff-negative' : ''}`}>
                                              {formatted}
                                            </td>
                                          );
                                        })()}
                                      </React.Fragment>
                                    );
                                  })}
                                </tr>
                              ));
                            })()}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <>
                        {/* ── Chart Mode: Summary Cards ── */}
                        <div className="chart-grid" style={{ marginBottom: '1.5rem' }}>
                          {/* Asset Class Lookthrough */}
                          <div className="chart-cell">
                            <Bar
                              data={{
                                labels: (() => {
                                  const allACs = new Set();
                                  comparisonTable.portfolios.forEach(p => {
                                    const lt = analyticsData.portfolios?.[p.key]?.lookthrough;
                                    if (lt) Object.entries(lt).forEach(([ac, v]) => { if (v > 0.001) allACs.add(ac); });
                                  });
                                  return [...allACs];
                                })(),
                                datasets: comparisonTable.portfolios.map(p => {
                                  const lt = analyticsData.portfolios?.[p.key]?.lookthrough || {};
                                  const allACs = new Set();
                                  comparisonTable.portfolios.forEach(pp => {
                                    const llt = analyticsData.portfolios?.[pp.key]?.lookthrough;
                                    if (llt) Object.entries(llt).forEach(([ac, v]) => { if (v > 0.001) allACs.add(ac); });
                                  });
                                  return {
                                    label: p.name,
                                    data: [...allACs].map(ac => lt[ac] ? +(lt[ac] * 100).toFixed(1) : 0),
                                    backgroundColor: p.colour,
                                  };
                                }),
                              }}
                              options={{
                                responsive: true,
                                maintainAspectRatio: true,
                                aspectRatio: 1.4,
                                plugins: {
                                  title: { display: true, text: 'Asset Class Lookthrough', color: '#072040', font: { size: 11, weight: 'bold' }, padding: { bottom: 4 } },
                                  legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 6, font: { size: 9 }, padding: 8 } },
                                  tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y?.toFixed(1) ?? '-'}%` } },
                                },
                                scales: {
                                  x: { grid: { display: false }, ticks: { font: { size: 8 }, maxRotation: 45, minRotation: 0 } },
                                  y: { title: { display: true, text: 'Allocation (%)', font: { size: 9 } }, grid: { color: 'rgba(0,0,0,0.06)' }, ticks: { font: { size: 8 }, callback: (v) => `${v}%` } },
                                },
                              }}
                            />
                          </div>

                          {/* Growth/Defence Bar */}
                          <div className="chart-cell">
                            <Bar
                              data={{
                                labels: comparisonTable.portfolios.filter(p => analyticsData.portfolios?.[p.key]).map(p => {
                                  const n = p.name;
                                  return n.length > 18 ? n.substring(0, 16) + '...' : n;
                                }),
                                datasets: [
                                  {
                                    label: 'Growth',
                                    data: comparisonTable.portfolios.filter(p => analyticsData.portfolios?.[p.key]).map(p => {
                                      const ge = analyticsData.portfolios?.[p.key]?.growth_exposure;
                                      return ge != null ? +(ge * 100).toFixed(1) : 0;
                                    }),
                                    backgroundColor: '#072040',
                                  },
                                  {
                                    label: 'Defensive',
                                    data: comparisonTable.portfolios.filter(p => analyticsData.portfolios?.[p.key]).map(p => {
                                      const ge = analyticsData.portfolios?.[p.key]?.growth_exposure;
                                      return ge != null ? +((1 - ge) * 100).toFixed(1) : 0;
                                    }),
                                    backgroundColor: '#599BE3',
                                  },
                                ],
                              }}
                              options={{
                                responsive: true,
                                maintainAspectRatio: true,
                                aspectRatio: 1.4,
                                indexAxis: 'y',
                                plugins: {
                                  title: { display: true, text: 'Growth / Defensive Split', color: '#072040', font: { size: 11, weight: 'bold' }, padding: { bottom: 4 } },
                                  legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 6, font: { size: 9 }, padding: 8 } },
                                  tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.x?.toFixed(1) ?? '-'}%` } },
                                },
                                scales: {
                                  x: { stacked: true, max: 100, ticks: { font: { size: 8 }, callback: (v) => `${v}%` }, grid: { color: 'rgba(0,0,0,0.06)' } },
                                  y: { stacked: true, grid: { display: false }, ticks: { font: { size: 9 } } },
                                },
                              }}
                            />
                          </div>
                        </div>

                        {/* ── Risk/Return metric bars: one bar per portfolio, in display order ── */}
                        <div className="chart-grid" style={{ marginBottom: '1.5rem' }}>
                          <MetricBarChart
                            title="Expected Return"
                            portfolios={comparisonTable.portfolios}
                            getValue={(p) => analyticsData.portfolios?.[p.key]?.expected_return}
                          />
                          <MetricBarChart
                            title="Volatility"
                            portfolios={comparisonTable.portfolios}
                            getValue={(p) => analyticsData.portfolios?.[p.key]?.volatility}
                          />
                          <MetricBarChart
                            title="Maximum Drawdown"
                            portfolios={comparisonTable.portfolios}
                            getValue={(p) => analyticsData.portfolios?.[p.key]?.stochastics?.metrics?.avg_max_drawdown}
                          />
                          <MetricBarChart
                            title="Return to Risk"
                            portfolios={comparisonTable.portfolios}
                            getValue={(p) => analyticsData.portfolios?.[p.key]?.stochastics?.metrics?.return_risk_ratio}
                          />
                        </div>

                        {/* Monte Carlo Fan Charts */}
                        <div className="chart-grid" style={{ marginBottom: '1.5rem' }}>
                          {comparisonTable.portfolios.map(p => {
                            const ad = analyticsData.portfolios?.[p.key];
                            if (!ad?.stochastics?.paths) return null;
                            return (
                              <div key={p.key} className="chart-cell">
                                <div style={{ fontSize: '0.8rem', fontWeight: 700, color: p.colour, marginBottom: '0.25rem', textAlign: 'center' }}>
                                  {p.name}
                                </div>
                                <div style={{ height: '300px' }}>
                                  <StochasticChart paths={ad.stochastics.paths} />
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Box Plot Comparison */}
                        {(() => {
                          const boxData = comparisonTable.portfolios
                            .filter(p => analyticsData.portfolios?.[p.key]?.box_plot_data?.length > 0)
                            .map(p => {
                              const bpd = analyticsData.portfolios[p.key].box_plot_data;
                              const blend = bpd.find(b => b.is_blend) || bpd[bpd.length - 1];
                              return { ...blend, name: p.name, colour: p.colour };
                            });
                          if (boxData.length === 0) return null;
                          return (
                            <div style={{ marginBottom: '1.5rem', height: '350px' }}>
                              <BoxPlotChart data={boxData} />
                            </div>
                          );
                        })()}

                        {/* Returns Histogram Overlay */}
                        {(() => {
                          const histograms = comparisonTable.portfolios
                            .filter(p => analyticsData.portfolios?.[p.key]?.returns_histograms?.length > 0)
                            .map(p => {
                              const hists = analyticsData.portfolios[p.key].returns_histograms;
                              const blend = hists.find(h => h.is_blend) || hists[hists.length - 1];
                              return { ...blend, name: p.name, colour: p.colour };
                            });
                          if (histograms.length === 0) return null;
                          return (
                            <div style={{ marginBottom: '1.5rem', height: '350px' }}>
                              <ReturnsHistogramChart histograms={histograms} />
                            </div>
                          );
                        })()}
                      </>
                    )}

                    {/* ── Stress Test Comparison Table (always shown) ── */}
                    {(() => {
                      const portfoliosWithStress = comparisonTable.portfolios.filter(
                        p => analyticsData.portfolios?.[p.key]?.stress_test_results?.length > 0
                      );
                      if (portfoliosWithStress.length === 0) return null;

                      // Get all unique events across portfolios
                      const allEvents = [];
                      const eventSet = new Set();
                      portfoliosWithStress.forEach(p => {
                        (analyticsData.portfolios[p.key].stress_test_results || []).forEach(e => {
                          if (!eventSet.has(e.event_name)) {
                            eventSet.add(e.event_name);
                            allEvents.push({ name: e.event_name, start: e.start_date, end: e.end_date });
                          }
                        });
                      });

                      return (
                        <div style={{ marginTop: '1.5rem' }}>
                          <div className="sac-header" style={{ marginBottom: '0.5rem' }}>
                            <span>Stress Test Results</span>
                          </div>

                          {analyticsViewMode === 'table' && (
                          <div style={{ overflowX: 'auto' }}>
                            <table className="factor-summary-table">
                              <thead>
                                <tr>
                                  <th style={{ textAlign: 'left' }}>Stress Event</th>
                                  <th style={{ textAlign: 'left', fontSize: '0.65rem' }}>Period</th>
                                  {portfoliosWithStress.map(p => (
                                    <React.Fragment key={p.key}>
                                      <th style={{ borderBottom: `3px solid ${p.colour}` }}>{p.name}</th>
                                      {benchmarkKey && p.key !== benchmarkKey && (
                                        <th className="diff-col" style={{ borderBottom: `3px solid ${p.colour}` }}>{'\u0394'}</th>
                                      )}
                                    </React.Fragment>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {allEvents.map(evt => {
                                  const bStress = benchmarkKey
                                    ? (analyticsData.portfolios?.[benchmarkKey]?.stress_test_results || []).find(e => e.event_name === evt.name)
                                    : null;
                                  return (
                                    <tr key={evt.name}>
                                      <td style={{ textAlign: 'left', fontWeight: 600 }}>{evt.name}</td>
                                      <td style={{ textAlign: 'left', fontSize: '0.65rem', color: 'var(--gray-color)' }}>
                                        {evt.start} to {evt.end}
                                      </td>
                                      {portfoliosWithStress.map(p => {
                                        const stressEvt = (analyticsData.portfolios[p.key].stress_test_results || []).find(e => e.event_name === evt.name);
                                        const val = stressEvt?.portfolio_return;
                                        const bVal = bStress?.portfolio_return;
                                        return (
                                          <React.Fragment key={p.key}>
                                            <td style={{
                                              fontWeight: 600,
                                              color: val != null ? (val >= 0 ? '#2d7a3a' : '#c0392b') : 'var(--gray-color)',
                                            }}>
                                              {val != null ? `${(val * 100).toFixed(2)}%` : '-'}
                                            </td>
                                            {benchmarkKey && p.key !== benchmarkKey && (() => {
                                              if (val == null || bVal == null) return <td className="diff-col">-</td>;
                                              const diff = (val - bVal) * 100;
                                              return (
                                                <td className={`diff-col ${diff > 0 ? 'diff-positive' : diff < 0 ? 'diff-negative' : ''}`}>
                                                  {diff > 0 ? '+' : ''}{diff.toFixed(2)}%
                                                </td>
                                              );
                                            })()}
                                          </React.Fragment>
                                        );
                                      })}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                          )}

                          {/* Stress Test Chart (chart mode only): one stacked bar chart per portfolio,
                              x-axis = events, segments = asset class contributions. */}
                          {analyticsViewMode === 'chart' && (
                            <div style={{ marginTop: '1rem' }}>
                              {portfoliosWithStress.map(p => {
                                const acSet = new Set();
                                allEvents.forEach(evt => {
                                  const se = (analyticsData.portfolios[p.key].stress_test_results || []).find(e => e.event_name === evt.name);
                                  (se?.top_contributors || []).forEach(tc => acSet.add(tc.asset_class));
                                });
                                const acList = [...acSet].sort();
                                if (acList.length === 0) return null;
                                return (
                                  <div key={p.key} style={{ marginBottom: '1.5rem' }}>
                                    <div style={{ fontSize: '0.85rem', fontWeight: 700, color: p.colour, textAlign: 'center', marginBottom: '0.25rem' }}>
                                      {p.name}
                                    </div>
                                    <div style={{ height: '320px' }}>
                                      <Bar
                                        data={{
                                          labels: allEvents.map(e => e.name.length > 25 ? e.name.substring(0, 23) + '...' : e.name),
                                          datasets: [
                                            {
                                              type: 'line',
                                              label: 'Total',
                                              data: allEvents.map(evt => {
                                                const se = (analyticsData.portfolios[p.key].stress_test_results || []).find(e => e.event_name === evt.name);
                                                return se?.portfolio_return != null ? +(se.portfolio_return * 100).toFixed(2) : null;
                                              }),
                                              borderColor: '#072040',
                                              backgroundColor: '#f0a500',
                                              borderWidth: 2,
                                              pointRadius: 6,
                                              pointHoverRadius: 8,
                                              pointBackgroundColor: '#f0a500',
                                              pointBorderColor: '#072040',
                                              pointBorderWidth: 2,
                                              pointStyle: 'circle',
                                              fill: false,
                                              tension: 0,
                                              order: -1,
                                            },
                                            ...acList.map((ac, i) => ({
                                              type: 'bar',
                                              label: ac,
                                              data: allEvents.map(evt => {
                                                const se = (analyticsData.portfolios[p.key].stress_test_results || []).find(e => e.event_name === evt.name);
                                                const tc = se?.top_contributors?.find(t => t.asset_class === ac);
                                                return tc ? +(tc.contribution * 100).toFixed(2) : 0;
                                              }),
                                              backgroundColor: getAssetClassColor(ac, i),
                                              stack: 'contrib',
                                              order: 10,
                                            })),
                                          ],
                                        }}
                                        options={{
                                          responsive: true,
                                          maintainAspectRatio: false,
                                          interaction: { mode: 'index', intersect: false },
                                          plugins: {
                                            legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 6, font: { size: 9 }, padding: 6 } },
                                            tooltip: {
                                              callbacks: {
                                                label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y?.toFixed(2) ?? '-'}%`,
                                              },
                                            },
                                          },
                                          scales: {
                                            x: { stacked: true, grid: { display: false }, ticks: { font: { size: 8 }, maxRotation: 45, minRotation: 0 } },
                                            y: { stacked: true, title: { display: true, text: 'Contribution (%)', font: { size: 9 } }, grid: { color: 'rgba(0,0,0,0.06)' }, ticks: { font: { size: 8 }, callback: (v) => `${v}%` } },
                                          },
                                        }}
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </>
                )}
              </>
            )}
          </div>
      )}

      {/* Risk Decomposition */}
      {comparisonTable && compareSection === 'risk' && (
        <>
          {analyticsData && !loadingAnalytics && (
            <div className="risk-decomposition-section">
              {/* View toggle */}
              <div className="d-flex justify-content-end mb-2">
                <div className="btn-group">
                  <button
                    className={`btn btn-sm ${riskViewMode === 'table' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    style={riskViewMode === 'table' ? { background: 'var(--primary-color)', borderColor: 'var(--primary-color)' } : {}}
                    onClick={() => setRiskViewMode('table')}
                  >
                    Table
                  </button>
                  <button
                    className={`btn btn-sm ${riskViewMode === 'chart' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    style={riskViewMode === 'chart' ? { background: 'var(--primary-color)', borderColor: 'var(--primary-color)' } : {}}
                    onClick={() => setRiskViewMode('chart')}
                  >
                    Chart
                  </button>
                </div>
              </div>

              {/* Volatility Contributions */}
              <div className="mb-4">
                <h6 style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--primary-color)', marginBottom: '0.5rem' }}>
                  Volatility Contribution by Sub Asset Class
                </h6>
                <p className="text-muted" style={{ fontSize: '0.78rem', marginBottom: '0.5rem' }}>
                  Shows each sub-asset class's percentage contribution to total portfolio volatility. Calculated as w<sub>i</sub> &times; (Cov &times; w)<sub>i</sub> / &sigma;<sub>p</sub>.
                </p>
                {riskViewMode === 'chart' ? (() => {
                  const allSacs = new Set();
                  comparisonTable.portfolios.forEach(p => {
                    const vc = analyticsData.portfolios?.[p.key]?.vol_contributions;
                    if (vc) vc.forEach(v => allSacs.add(v.asset_class));
                  });
                  const sacList = [...allSacs].sort();
                  return (
                    <div style={{ height: '360px' }}>
                      <Bar
                        data={{
                          labels: comparisonTable.portfolios.map(p => p.name),
                          datasets: sacList.map((sac, i) => ({
                            label: sacDisplayNames[sac] || sac,
                            data: comparisonTable.portfolios.map(p => {
                              const vc = analyticsData.portfolios?.[p.key]?.vol_contributions;
                              const e = vc?.find(v => v.asset_class === sac);
                              return e ? +e.contribution_pct.toFixed(2) : 0;
                            }),
                            backgroundColor: getAssetClassColor(sacDisplayNames[sac] || sac, i),
                          })),
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 6, font: { size: 9 }, padding: 6 } },
                            tooltip: {
                              callbacks: {
                                label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y?.toFixed(1) ?? '-'}%`,
                                footer: (items) => `Total: ${items.reduce((s, it) => s + (it.parsed.y || 0), 0).toFixed(1)}%`,
                              },
                            },
                          },
                          scales: {
                            x: { stacked: true, grid: { display: false }, ticks: { font: { size: 9 } } },
                            y: { stacked: true, title: { display: true, text: '% of total volatility', font: { size: 9 } }, grid: { color: 'rgba(0,0,0,0.06)' }, ticks: { font: { size: 8 }, callback: (v) => `${v}%` } },
                          },
                        }}
                      />
                    </div>
                  );
                })() : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="holdings-table">
                    <thead>
                      <tr>
                        <th className="name-col" colSpan={2}>Sub Asset Class</th>
                        {comparisonTable.portfolios.map(p => (
                          <th key={p.key} className="weight-col" style={{ borderBottom: `3px solid ${p.colour}` }}>
                            <div>{p.name}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // Collect all SACs across all portfolios' vol contributions
                        const allSacs = new Set();
                        comparisonTable.portfolios.forEach(p => {
                          const vc = analyticsData.portfolios?.[p.key]?.vol_contributions;
                          if (vc) vc.forEach(v => allSacs.add(v.asset_class));
                        });
                        const sacList = [...allSacs].sort();
                        return sacList.map(sac => (
                          <tr key={sac} className="holding-row">
                            <td className="name-col" colSpan={2}>{sac}</td>
                            {comparisonTable.portfolios.map(p => {
                              const vc = analyticsData.portfolios?.[p.key]?.vol_contributions;
                              const entry = vc?.find(v => v.asset_class === sac);
                              return (
                                <td key={p.key} className="weight-col">
                                  {entry ? `${entry.contribution_pct.toFixed(1)}%` : <span style={{ color: 'var(--gray-color)' }}>-</span>}
                                </td>
                              );
                            })}
                          </tr>
                        ));
                      })()}
                      <tr className="total-row">
                        <td colSpan={2}>Total Volatility</td>
                        {comparisonTable.portfolios.map(p => {
                          const vol = analyticsData.portfolios?.[p.key]?.volatility;
                          return (
                            <td key={p.key} className="weight-col">
                              {vol != null ? `${(vol * 100).toFixed(2)}%` : '-'}
                            </td>
                          );
                        })}
                      </tr>
                    </tbody>
                  </table>
                </div>
                )}
              </div>

              {/* Tracking Error (when benchmark selected) */}
              {benchmarkKey && (() => {
                const nonBenchmark = comparisonTable.portfolios.filter(p =>
                  p.key !== benchmarkKey && analyticsData.portfolios?.[p.key]?.tracking_error_vs?.[benchmarkKey]?.contributions?.length > 0
                );
                if (nonBenchmark.length === 0) return null;
                const benchmarkName = comparisonTable.portfolios.find(p => p.key === benchmarkKey)?.name || 'Benchmark';
                return (
                  <div className="mb-4">
                    <h6 style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--primary-color)', marginBottom: '0.5rem' }}>
                      Asset Allocation Tracking Error vs {benchmarkName}
                    </h6>
                    <p className="text-muted" style={{ fontSize: '0.78rem', marginBottom: '0.5rem' }}>
                      Forward-looking tracking error from asset allocation differences. TE = &radic;(tilt&prime; &times; Cov &times; tilt), where tilts are the sub-asset class weight differences between each portfolio and the benchmark. Contribution shows each asset class's marginal impact on total tracking error.
                    </p>
                    {riskViewMode === 'chart' && (() => {
                      const allAcs = new Set();
                      nonBenchmark.forEach(p => {
                        const te = analyticsData.portfolios?.[p.key]?.tracking_error_vs?.[benchmarkKey];
                        (te?.contributions || []).forEach(c => allAcs.add(c.asset_class));
                      });
                      const acList = [...allAcs].sort();
                      return (
                        <div style={{ height: '360px' }}>
                          <Bar
                            data={{
                              labels: nonBenchmark.map(p => p.name),
                              datasets: acList.map((ac, i) => ({
                                label: ac,
                                data: nonBenchmark.map(p => {
                                  const te = analyticsData.portfolios?.[p.key]?.tracking_error_vs?.[benchmarkKey];
                                  const e = te?.contributions?.find(c => c.asset_class === ac);
                                  return e ? +e.contribution_pct.toFixed(2) : 0;
                                }),
                                backgroundColor: getAssetClassColor(ac, i),
                              })),
                            }}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              plugins: {
                                legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 6, font: { size: 9 }, padding: 6 } },
                                tooltip: {
                                  callbacks: {
                                    label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y?.toFixed(1) ?? '-'}%`,
                                    footer: (items) => `Total: ${items.reduce((s, it) => s + (it.parsed.y || 0), 0).toFixed(1)}%`,
                                  },
                                },
                              },
                              scales: {
                                x: { stacked: true, grid: { display: false }, ticks: { font: { size: 9 } } },
                                y: { stacked: true, title: { display: true, text: '% of total TE', font: { size: 9 } }, grid: { color: 'rgba(0,0,0,0.06)' }, ticks: { font: { size: 8 }, callback: (v) => `${v}%` } },
                              },
                            }}
                          />
                        </div>
                      );
                    })()}
                    {riskViewMode === 'table' && nonBenchmark.map(p => {
                      const te = analyticsData.portfolios?.[p.key]?.tracking_error_vs?.[benchmarkKey];
                      if (!te?.contributions?.length) return null;
                      return (
                        <div key={p.key} className="mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-2" style={{ fontSize: '0.82rem' }}>
                            <span style={{ fontWeight: 600, color: p.colour }}>{p.name}</span>
                            <span style={{ color: 'var(--gray-color)' }}>
                              Total TE: <strong style={{ color: 'var(--primary-color)' }}>{(te.tracking_error * 100).toFixed(2)}%</strong>
                            </span>
                          </div>
                          <table className="table table-sm mb-0" style={{ fontSize: '0.8rem' }}>
                            <thead>
                              <tr style={{ backgroundColor: 'var(--light-color)' }}>
                                <th style={{ padding: '0.4rem 0.6rem', color: 'var(--primary-color)', fontWeight: 600 }}>Asset Class</th>
                                <th style={{ padding: '0.4rem 0.6rem', color: 'var(--primary-color)', fontWeight: 600, textAlign: 'right' }}>Tilt (pp)</th>
                                <th style={{ padding: '0.4rem 0.6rem', color: 'var(--primary-color)', fontWeight: 600, textAlign: 'right' }}>TE Contribution</th>
                                <th style={{ padding: '0.4rem 0.6rem', color: 'var(--primary-color)', fontWeight: 600, textAlign: 'right' }}>% of Total TE</th>
                                <th style={{ padding: '0.4rem 0.6rem', width: '20%' }}></th>
                              </tr>
                            </thead>
                            <tbody>
                              {te.contributions.map((row, i) => {
                                const barWidth = Math.max(0, Math.min(100, Math.abs(row.contribution_pct)));
                                const isPos = row.tilt >= 0;
                                return (
                                  <tr key={row.asset_class} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                                    <td style={{ padding: '0.35rem 0.6rem', fontWeight: 500 }}>{row.asset_class}</td>
                                    <td style={{ padding: '0.35rem 0.6rem', textAlign: 'right', fontWeight: 600, color: isPos ? 'var(--info-color)' : 'var(--secondary-color)', fontVariantNumeric: 'tabular-nums' }}>
                                      {row.tilt >= 0 ? '+' : ''}{row.tilt.toFixed(2)}pp
                                    </td>
                                    <td style={{ padding: '0.35rem 0.6rem', textAlign: 'right', fontWeight: 600, color: 'var(--primary-color)', fontVariantNumeric: 'tabular-nums' }}>
                                      {(row.contribution * 100).toFixed(2)}%
                                    </td>
                                    <td style={{ padding: '0.35rem 0.6rem', textAlign: 'right', color: 'var(--gray-color)', fontVariantNumeric: 'tabular-nums' }}>
                                      {row.contribution_pct.toFixed(1)}%
                                    </td>
                                    <td style={{ padding: '0.35rem 0.6rem' }}>
                                      <div style={{ background: 'var(--light-color)', borderRadius: 3, height: 8, overflow: 'hidden' }}>
                                        <div style={{ width: `${barWidth}%`, height: '100%', backgroundColor: 'var(--info-color)', borderRadius: 3 }} />
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Fund-Level Tracking Error by Equity Sleeve */}
              {(() => {
                const hasFundTE = comparisonTable.portfolios.some(p =>
                  analyticsData.portfolios?.[p.key]?.fund_te
                );
                if (!hasFundTE) return null;

                // Funds excluded from the calc for missing/incomplete data (union across portfolios)
                const missMap = {};
                comparisonTable.portfolios.forEach(p => {
                  const mf = analyticsData.portfolios?.[p.key]?.fund_te?._missing_funds;
                  if (mf) mf.forEach(m => { missMap[m.tr_code] = m; });
                });
                const missList = Object.values(missMap);
                const missingBanner = missList.length > 0 ? (
                  <div key="fundte-missing" className="alert alert-warning" style={{ fontSize: '0.78rem', padding: '0.5rem 0.75rem', marginBottom: '0.75rem' }}>
                    <strong>&#9888; Missing data (excluded from tracking error):</strong>{' '}
                    {missList.map(m => `${m.fund_name}${m.last_month ? ` (data to ${m.last_month})` : ' (no return data)'}`).join('; ')}.
                    {' '}Left out of the calculation until their return history is complete and current.
                  </div>
                ) : null;

                // Collect all sleeves (skip the reserved _missing_funds key)
                const allSleeves = new Set();
                comparisonTable.portfolios.forEach(p => {
                  const ft = analyticsData.portfolios?.[p.key]?.fund_te;
                  if (ft) Object.keys(ft).filter(k => k !== '_missing_funds').forEach(s => allSleeves.add(s));
                });

                const sleeves = [...allSleeves].sort().map(sleeve => {
                  // Collect all fund names across portfolios for this sleeve
                  const allFunds = new Set();
                  comparisonTable.portfolios.forEach(p => {
                    const fc = analyticsData.portfolios?.[p.key]?.fund_te?.[sleeve]?.fund_contributions;
                    if (fc) Object.keys(fc).forEach(f => allFunds.add(f));
                  });
                  const fundList = [...allFunds].sort();
                  if (fundList.length === 0) return null;

                  return (
                    <div key={sleeve} className="mb-4">
                      <h6 style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--primary-color)', marginBottom: '0.5rem' }}>
                        Fund Selection Tracking Error — {sleeve === 'EQ_AU' ? 'Australian Equities' : 'International Equities'}
                      </h6>
                      <p className="text-muted" style={{ fontSize: '0.78rem', marginBottom: '0.5rem' }}>
                        Realised tracking error from fund alpha dispersion within the equity sleeve. TE = &radic;(w&prime; &times; &Sigma; &times; w) where &Sigma; is the trailing 36-month fund alpha covariance matrix. Fund contributions show each fund's marginal contribution to sleeve TE.
                      </p>
                      {riskViewMode === 'chart' ? (
                        <div style={{ height: '360px' }}>
                          <Bar
                            data={{
                              labels: comparisonTable.portfolios.map(p => p.name),
                              datasets: fundList.map((fund, i) => ({
                                label: fund,
                                data: comparisonTable.portfolios.map(p => {
                                  const fc = analyticsData.portfolios?.[p.key]?.fund_te?.[sleeve]?.fund_contributions;
                                  return fc?.[fund] != null ? +fc[fund].toFixed(2) : 0;
                                }),
                                backgroundColor: getAssetClassColor(fund, i),
                              })),
                            }}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              plugins: {
                                legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 6, font: { size: 8 }, padding: 6 } },
                                tooltip: {
                                  callbacks: {
                                    label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y?.toFixed(1) ?? '-'}%`,
                                    footer: (items) => `Total: ${items.reduce((s, it) => s + (it.parsed.y || 0), 0).toFixed(1)}%`,
                                  },
                                },
                              },
                              scales: {
                                x: { stacked: true, grid: { display: false }, ticks: { font: { size: 9 } } },
                                y: { stacked: true, title: { display: true, text: '% of sleeve TE', font: { size: 9 } }, grid: { color: 'rgba(0,0,0,0.06)' }, ticks: { font: { size: 8 }, callback: (v) => `${v}%` } },
                              },
                            }}
                          />
                        </div>
                      ) : (
                      <div style={{ overflowX: 'auto' }}>
                        <table className="holdings-table">
                          <thead>
                            <tr>
                              <th className="name-col" colSpan={2}>Fund</th>
                              {comparisonTable.portfolios.map(p => (
                                <th key={p.key} className="weight-col" style={{ borderBottom: `3px solid ${p.colour}` }}>
                                  <div>{p.name}</div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {fundList.map(fund => (
                              <tr key={fund} className="holding-row">
                                <td className="name-col" colSpan={2} style={{ fontSize: '0.78rem' }}>{fund}</td>
                                {comparisonTable.portfolios.map(p => {
                                  const fc = analyticsData.portfolios?.[p.key]?.fund_te?.[sleeve]?.fund_contributions;
                                  const val = fc?.[fund];
                                  return (
                                    <td key={p.key} className="weight-col">
                                      {val != null ? `${val.toFixed(1)}%` : <span style={{ color: 'var(--gray-color)' }}>-</span>}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                            <tr className="total-row">
                              <td colSpan={2}>Sleeve TE (annualised)</td>
                              {comparisonTable.portfolios.map(p => {
                                const te = analyticsData.portfolios?.[p.key]?.fund_te?.[sleeve]?.te;
                                return (
                                  <td key={p.key} className="weight-col">
                                    {te != null ? `${te.toFixed(2)}%` : '-'}
                                  </td>
                                );
                              })}
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      )}
                    </div>
                  );
                });

                return <>{missingBanner}{sleeves}</>;
              })()}
            </div>
          )}
        </>
      )}

      {/* Backtesting */}
      {comparisonTable && compareSection === 'backtest' && (
        <div className="backtest-section">
          {loadingBacktest && (
            <div className="d-flex align-items-center gap-2 mb-3" style={{ fontSize: '0.85rem' }}>
              <div className="spinner-border spinner-border-sm text-primary" role="status" />
              <span className="text-muted">Backtesting portfolios over the shared date intersection...</span>
            </div>
          )}

          {backtestError && !loadingBacktest && (
            <div className="alert alert-warning" style={{ fontSize: '0.85rem' }}>
              {backtestError}
            </div>
          )}

          {backtestData && !loadingBacktest && (() => {
            const ordered = comparisonTable.portfolios.map(p => {
              const found = backtestData.portfolios.find(b => b.key === p.key);
              return { meta: p, result: found };
            });

            // Build chart labels from the first portfolio that has a series
            const baseSeries = ordered.find(o => o.result?.cumulative_series?.length)?.result?.cumulative_series || [];
            const labels = baseSeries.map(pt => {
              const d = new Date(pt.date);
              return d.toLocaleDateString('en-AU', { month: 'short', year: '2-digit' });
            });
            const labelMonthKeys = baseSeries.map(pt => (pt.date || '').slice(0, 7));

            const datasets = ordered
              .filter(o => o.result?.cumulative_series?.length)
              .map(o => ({
                label: o.meta.name,
                data: o.result.cumulative_series.map(pt => pt.value),
                borderColor: o.meta.colour,
                backgroundColor: o.meta.colour,
                borderWidth: 2,
                fill: false,
                tension: 0.1,
                pointRadius: 0,
                pointHoverRadius: 4,
              }));

            // Append actual-performance overlays (dashed) for selected model portfolios
            const overlayDatasets = overlayPortfolioCodes
              .map(code => {
                const s = overlaySeries[code];
                const meta = eligibleOverlayPortfolios.find(p => p.portfolio_code === code);
                return { code, s, meta };
              })
              .filter(x => x.s?.cumulative_series?.length && x.meta)
              .map(({ s, meta }) => {
                const byMonth = Object.fromEntries(
                  s.cumulative_series.map(pt => [(pt.date || '').slice(0, 7), pt.value])
                );
                return {
                  label: `${meta.name} (Actual)`,
                  data: labelMonthKeys.map(k => (k in byMonth ? byMonth[k] : null)),
                  borderColor: meta.colour,
                  backgroundColor: meta.colour,
                  borderDash: [6, 4],
                  borderWidth: 2,
                  fill: false,
                  tension: 0.1,
                  pointRadius: 0,
                  pointHoverRadius: 4,
                  spanGaps: false,
                };
              });
            datasets.push(...overlayDatasets);

            const overlayMessages = overlayPortfolioCodes
              .map(code => {
                const s = overlaySeries[code];
                const meta = eligibleOverlayPortfolios.find(p => p.portfolio_code === code);
                return { code, s, meta };
              })
              .filter(x => x.s && (x.s.error || x.s.warning));

            // Overlay columns for the metrics table (any overlay with metrics computed)
            const overlayColumns = overlayPortfolioCodes
              .map(code => {
                const s = overlaySeries[code];
                const meta = eligibleOverlayPortfolios.find(p => p.portfolio_code === code);
                return { code, s, meta };
              })
              .filter(x => x.s?.metrics && x.meta && !x.s.error);

            const allExcluded = ordered.flatMap(o =>
              (o.result?.excluded_funds || []).map(code => ({ name: o.meta.name, code }))
            );

            return (
              <>
                <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
                  <h6 className="mb-0" style={{ color: 'var(--primary-color)', fontWeight: 700, fontSize: '0.9rem' }}>
                    Cumulative Performance
                  </h6>
                  <span className="text-muted" style={{ fontSize: '0.78rem' }}>
                    {backtestData.common_start_date} to {backtestData.common_end_date} ({backtestData.num_months} months)
                  </span>
                </div>

                {backtestData.start_date_drivers?.length > 0 && (
                  <div className="mb-3" style={{ fontSize: '0.78rem', color: 'var(--gray-color)' }}>
                    <strong style={{ color: 'var(--primary-color)' }}>Start date determined by:</strong>{' '}
                    {backtestData.start_date_drivers.map((d, i) => (
                      <span key={d.fund_code}>
                        {d.fund_name} ({d.fund_code})
                        {i < backtestData.start_date_drivers.length - 1 ? ', ' : ''}
                      </span>
                    ))}
                  </div>
                )}

                {overlayMessages.length > 0 && (
                  <div className="mb-2">
                    {overlayMessages.map(({ code, s, meta }) => (
                      <div
                        key={code}
                        className={`alert ${s.error ? 'alert-warning' : 'alert-info'} py-1 mb-1`}
                        style={{ fontSize: '0.78rem' }}
                      >
                        <strong>{meta?.name || code}:</strong> {s.error || s.warning}
                      </div>
                    ))}
                  </div>
                )}

                {/* Chart */}
                <div style={{ height: '420px', marginBottom: '1.5rem' }}>
                  <Line
                    data={{ labels, datasets }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      interaction: { mode: 'index', intersect: false },
                      plugins: {
                        legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 8, font: { size: 10 }, padding: 8 } },
                        tooltip: {
                          callbacks: {
                            label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y?.toFixed(2) ?? '-'}%`,
                          },
                        },
                      },
                      scales: {
                        x: { grid: { display: false }, ticks: { font: { size: 9 }, maxTicksLimit: 12 } },
                        y: {
                          title: { display: true, text: 'Cumulative return', font: { size: 9 } },
                          grid: { color: 'rgba(0,0,0,0.06)' },
                          ticks: { font: { size: 9 }, callback: (v) => `${v}%` },
                        },
                      },
                    }}
                  />
                </div>

                {/* Metrics table */}
                <div style={{ overflowX: 'auto' }}>
                  <table className="factor-summary-table">
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left' }}>Metric</th>
                        {ordered.map(o => (
                          <th key={o.meta.key} style={{ borderBottom: `3px solid ${o.meta.colour}` }}>
                            {o.meta.name}
                          </th>
                        ))}
                        {overlayColumns.map(({ code, meta }) => (
                          <th
                            key={`overlay-${code}`}
                            style={{ borderBottom: `3px dashed ${meta.colour}` }}
                          >
                            {meta.name}
                            <div style={{ fontSize: '0.65rem', fontWeight: 500, color: 'var(--gray-color)' }}>
                              (Actual)
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { key: 'total_return', label: 'Total Return', suffix: '%' },
                        { key: 'annualised_return', label: 'Annualised Return', suffix: '%' },
                        { key: 'volatility', label: 'Volatility', suffix: '%' },
                        { key: 'sharpe_ratio', label: 'Return / Risk', suffix: '' },
                        { key: 'max_drawdown', label: 'Max Drawdown', suffix: '%' },
                      ].map(metric => (
                        <tr key={metric.key}>
                          <td style={{ textAlign: 'left' }}>{metric.label}</td>
                          {ordered.map(o => {
                            const v = o.result?.metrics?.[metric.key];
                            return (
                              <td key={o.meta.key}>
                                {v == null ? <span style={{ color: 'var(--gray-color)' }}>-</span>
                                  : `${v.toFixed(2)}${metric.suffix}`}
                              </td>
                            );
                          })}
                          {overlayColumns.map(({ code, s }) => {
                            const v = s?.metrics?.[metric.key];
                            return (
                              <td key={`overlay-${code}`}>
                                {v == null ? <span style={{ color: 'var(--gray-color)' }}>-</span>
                                  : `${v.toFixed(2)}${metric.suffix}`}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {allExcluded.length > 0 && (
                  <div style={{ marginTop: '0.75rem', fontSize: '0.72rem', color: 'var(--gray-color)', borderTop: '1px solid var(--light-color)', paddingTop: '0.5rem' }}>
                    <strong>Excluded funds (no return data):</strong>{' '}
                    {allExcluded.map((f, i) => (
                      <span key={i}>{f.code} ({f.name}){i < allExcluded.length - 1 ? ', ' : ''}</span>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default PortfolioComparison;
